import { useEffect, useMemo, useRef, useState } from "react";
import {
  Barcode,
  Camera,
  Edit3,
  Eye,
  Menu,
  Package,
  Plus,
  Printer,
  ShieldCheck,
  Save,
  Search,
  ScanLine,
  ShoppingBag,
  Trash2,
  X,
} from "lucide-react";
import { useReactToPrint } from "react-to-print";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { API_BASE_URL } from "../../config/api";
import Sidebar from "../../components/Sidebar";
import "./AdminSales.css";

const initialForm = {
  productId: "", // device_purchases.id in the selector

  productName: "",
  barcode: "",
  quantity: 1,
  unitPrice: "",
  purchasePrice: "",
  discountPercent: "0",
  discountLek: "0",
  exchangeRate: "",
  euroReceived: "",
  imei: "",
  warrantyMonths: "0",
  customerName: "",
  customerPhone: "",
  customerAddress: "",
  customerEmail: "",
  notes: "",
};

const formatPrice = (value) =>
  new Intl.NumberFormat("sq-AL", {
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const getProductName = (product) =>
  product?.name || product?.title || product?.model || "Produkt pa emër";

const getProductPrice = (product) =>
  Number(
    product?.price ??
    product?.selling_price ??
    product?.sale_price ??
    0
  );

const getProductPurchasePrice = (product) =>
  Number(product?.purchase_price || 0);

const getProductBonusPercent = (product) =>
  Number(product?.bonus_share_percent || 0);

const getProductProfit = (product) =>
  Math.max(
    0,
    getProductPrice(product) -
    getProductPurchasePrice(product)
  );

const getProductBonus = (product, quantity = 1) =>
  getProductProfit(product) *
  (getProductBonusPercent(product) / 100) *
  Number(quantity || 0);


const getProductOfferDiscount = (product) => {
  const isOffer =
    Number(product?.is_offer || 0) === 1;

  if (!isOffer) {
    return 0;
  }

  return Math.min(
    100,
    Math.max(
      0,
      Number(product?.discount || 0)
    )
  );
};

const getProductBasePrice = (product) => {
  const normalPrice =
    getProductPrice(product);

  const offerDiscount =
    getProductOfferDiscount(product);

  return (
    normalPrice *
    (1 - offerDiscount / 100)
  );
};

const getProductMaxDiscount = (product) =>
  Number(product?.max_discount_percent || 0);

const getProductMinAllowedPrice = (product) => {
  const basePrice =
    getProductBasePrice(product);

  const maxDiscount =
    getProductMaxDiscount(product);

  return (
    basePrice *
    (1 - maxDiscount / 100)
  );
};

const getProductStock = (product) =>
  Number(
    product?.stock ??
    product?.quantity ??
    product?.stock_quantity ??
    0
  );

const readResponse = async (response) => {
  const text = await response.text();

  if (!text) return {};

  try {
    return JSON.parse(text);
  } catch {
    return { message: text };
  }
};

const getAuthToken = () =>
  String(localStorage.getItem("authToken") || "")
    .replace(/^"|"$/g, "")
    .replace(/^Bearer\s+/i, "")
    .trim();

export default function AdminSales() {
  const loggedUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user")) || {};
    } catch {
      return {};
    }
  }, []);

  const [products, setProducts] = useState([]);
  const [editingDevice, setEditingDevice] = useState(null);
  const [editingDeviceBusy, setEditingDeviceBusy] = useState(false);
  const [sales, setSales] = useState([]);
  const [customerDirectory, setCustomerDirectory] = useState([]);
  const [customerSearchOpen, setCustomerSearchOpen] = useState(false);
  const [customerHistory, setCustomerHistory] = useState(null);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [customerError, setCustomerError] = useState("");
  const [customWarrantyTerms, setCustomWarrantyTerms] = useState(() => {
    try { return JSON.parse(localStorage.getItem("masterphoneWarrantyTerms") || "[]"); }
    catch { return []; }
  });
  const [newWarrantyTerm, setNewWarrantyTerm] = useState("");
  const [showAddWarranty, setShowAddWarranty] = useState(false);
  const invoiceRef = useRef(null);
  const warrantyRef = useRef(null);
  const productPickerRef = useRef(null);
  const barcodeVideoRef = useRef(null);
  const barcodeScannerControlsRef = useRef(null);
  const [selectedSale, setSelectedSale] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [manualProduct, setManualProduct] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [search, setSearch] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingSales, setLoadingSales] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mobileDetailSale, setMobileDetailSale] = useState(null);
  const [detailItems, setDetailItems] = useState([]);
  useEffect(() => {
    if (!mobileDetailSale?.id) { setDetailItems([]); return; }
    let active = true;
    fetch(`${API_BASE_URL}/api/sales/${mobileDetailSale.id}/items`, { headers: getAuthHeaders() })
      .then(async response => response.ok ? readResponse(response) : { items: [] })
      .then(data => { if (active) setDetailItems(Array.isArray(data.items) ? data.items : []); })
      .catch(() => { if (active) setDetailItems([]); });
    return () => { active = false; };
  }, [mobileDetailSale?.id]);
  const [barcodeScannerOpen, setBarcodeScannerOpen] = useState(false);
  const [barcodeScannerBusy, setBarcodeScannerBusy] = useState(false);
  const [barcodeScannerError, setBarcodeScannerError] = useState("");
  const [barcodeLookupBusy, setBarcodeLookupBusy] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  const getAuthHeaders = () => {
    const token = getAuthToken();

    return {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  };

  const handleUnauthorized = (response) => {
    if (response.status !== 401 && response.status !== 403) return false;

    setMessage({
      type: "error",
      text: "Sesioni ka përfunduar ose token-i nuk është i vlefshëm. Hyr përsëri në sistem.",
    });

    return true;
  };

  const fetchProducts = async () => {
    try {
      setLoadingProducts(true);


      const response = await fetch(`${API_BASE_URL}/api/sales/available-devices`, {
        headers: getAuthHeaders(),
      });

      const data = await readResponse(response);




      if (handleUnauthorized(response)) return;

      if (!response.ok) {
        throw new Error(
          data?.details ||
          data?.error ||
          data?.message ||
          "Produktet nuk mund të ngarkoheshin."
        );
      }

      setProducts(
        Array.isArray(data) ? data : data.products || data.data || []
      );
    } catch (error) {
      console.error("Gabim gjatë ngarkimit të pajisjeve:", error);
      setMessage({ type: "error", text: error.message || "Pajisjet nuk mund të ngarkoheshin." });
    } finally {
      setLoadingProducts(false);
    }
  };

  const fetchSales = async () => {
    try {
      setLoadingSales(true);
      setMessage({ type: "", text: "" });

      const response = await fetch(`${API_BASE_URL}/api/sales`, {
        headers: getAuthHeaders(),
      });

      const data = await readResponse(response);

      if (handleUnauthorized(response)) return;

      if (!response.ok) {
        throw new Error(
          data?.details ||
          data?.error ||
          data?.message ||
          "Shitjet nuk mund të ngarkoheshin."
        );
      }

      setSales(Array.isArray(data) ? data : data.sales || data.data || []);
    } catch (error) {
      setMessage({
        type: "error",
        text: error.message || "Shitjet nuk mund të ngarkoheshin.",
      });
    } finally {
      setLoadingSales(false);
    }
  };

  useEffect(() => {
    const token = getAuthToken();

    if (!token) {
      setMessage({
        type: "error",
        text: "Nuk u gjet token-i i hyrjes. Dil dhe identifikohu përsëri.",
      });
      setLoadingSales(false);
      return;
    }

    fetchProducts();
    fetchSales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load customers from the central customer registry. The endpoint supports search and pagination.
  useEffect(() => {
    if (!showModal) return;
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        setCustomerLoading(true);
        setCustomerError("");
        const params = new URLSearchParams({ search: form.customerName.trim(), limit: "100" });
        const response = await fetch(`${API_BASE_URL}/api/customers?${params}`, {
          headers: getAuthHeaders(), signal: controller.signal,
        });
        if (!response.ok) throw new Error(`API klientëve: ${response.status}`);
        const payload = await readResponse(response);
        const rows = Array.isArray(payload) ? payload : payload.customers || payload.data || [];
        setCustomerDirectory(Array.isArray(rows) ? rows : []);
      } catch (error) {
        if (error.name !== "AbortError") setCustomerError("Klientët nuk u ngarkuan nga API. Kontrollo /api/customers.");
      } finally {
        if (!controller.signal.aborted) setCustomerLoading(false);
      }
    }, 220);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [showModal, form.customerName]);

  const normalizeCustomer = (customer) => {
    const fullName = String(customer.full_name || customer.name ||
      [customer.first_name, customer.last_name].filter(Boolean).join(" ")).trim();
    const phone = String(customer.phone || customer.customer_phone || "").trim();
    const customerSales = sales.filter(s =>
      (phone && String(s.customer_phone || "").replace(/\D/g, "") === phone.replace(/\D/g, "")) ||
      (!phone && fullName && String(s.customer_name || "").trim().toLowerCase() === fullName.toLowerCase())
    );
    const rawStatus = String(customer.status || customer.customer_status || "").toLowerCase();
    const status = rawStatus.includes("poten") ? "Potencial" :
      rawStatus.includes("kth") || rawStatus.includes("return") ? "Klient i kthyer" :
      customerSales.length >= 2 ? "Klient i kthyer" :
      customerSales.length === 1 ? "Klient ekzistues" : "Potencial";
    return { ...customer, fullName, phone, status, customerSales };
  };

  const visibleCustomers = useMemo(() => {
    const query = form.customerName.trim().toLocaleLowerCase("sq");
    return customerDirectory.map(normalizeCustomer)
      .filter(c => !query || `${c.fullName} ${c.phone}`.toLocaleLowerCase("sq").includes(query))
      .slice(0, 50);
  }, [customerDirectory, form.customerName, sales]);

  const chooseCustomer = (customer) => {
    setForm(previous => ({ ...previous,
      customerName: customer.fullName,
      customerPhone: customer.phone,
      customerEmail: customer.email || previous.customerEmail,
      customerAddress: customer.address || previous.customerAddress,
    }));
    setCustomerSearchOpen(false);
  };

  const warrantyOptions = useMemo(() => Array.from(new Set([
    0, 3, 6, 12, 24, 36,
    ...customWarrantyTerms.map(Number), Number(form.warrantyMonths || 0),
  ])).filter(v => Number.isInteger(v) && v >= 0).sort((a, b) => a - b),
  [customWarrantyTerms, form.warrantyMonths]);

  const addWarrantyTerm = () => {
    const months = Number(newWarrantyTerm);
    if (!Number.isInteger(months) || months < 1 || months > 240) {
      setMessage({ type: "error", text: "Vendos një afat të plotë nga 1 deri në 240 muaj." });
      return;
    }
    const next = Array.from(new Set([...customWarrantyTerms.map(Number), months])).sort((a, b) => a - b);
    setCustomWarrantyTerms(next);
    localStorage.setItem("masterphoneWarrantyTerms", JSON.stringify(next));
    setForm(previous => ({ ...previous, warrantyMonths: String(months) }));
    setNewWarrantyTerm("");
    setShowAddWarranty(false);
  };

  const filteredSales = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) {
      return sales;
    }

    return sales.filter((sale) => {
      const searchable = [
        sale.product_name,
        sale.barcode,
        sale.seller_name,
        sale.username,
        sale.notes,
        sale.imei,
        sale.customer_name,
        sale.customer_phone,
        sale.customer_address,
        sale.customer_email,
        sale.created_at,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchable.includes(query);
    });
  }, [sales, search]);

  const availableProducts = useMemo(() => {
    const rows = products.filter((device) => getProductStock(device) > 0);
    if (editingId && editingDevice && !rows.some((device) =>
      String(device.device_purchase_id) === String(editingDevice.device_purchase_id))) {
      return [...rows, editingDevice];
    }
    return rows;
  }, [products, editingId, editingDevice]);

  const selectedProduct = useMemo(() =>
    availableProducts.find((device) =>
      String(device.device_purchase_id) === String(form.productId)),
    [availableProducts, form.productId]);

  const maxDiscountPercent = selectedProduct
    ? getProductMaxDiscount(selectedProduct)
    : 0;

  // Çmimi merret nga device_purchases.sale_price.
  const productNormalPrice = selectedProduct
    ? getProductPrice(selectedProduct)
    : 0;

  // OFERTA AUTOMATIKE e regjistruar te produkti
  const automaticOfferPercent = selectedProduct
    ? getProductOfferDiscount(selectedProduct)
    : 0;

  // Çmimi pas ofertës automatike.
  // Nëse s'ka ofertë = product.price.
  const stockBasePrice = selectedProduct
    ? getProductBasePrice(selectedProduct)
    : 0;

  // Ulja që propozon shitësi.
  // Ekziston si për Stock, ashtu edhe për Manual.
  const discountBasePrice = manualProduct ? Number(form.unitPrice || 0) : stockBasePrice;
  const proposedDiscountLek = Number(form.discountLek || 0);
  const proposedDiscountPercent = discountBasePrice > 0
    ? (proposedDiscountLek / discountBasePrice) * 100 : 0;
  const currentLineTotal = Number(form.quantity || 0) * Math.max(0, discountBasePrice - proposedDiscountLek);
  const invoiceTotal = invoiceItems.reduce((sum, item) => sum + item.total, 0) +
    (form.productName.trim() ? currentLineTotal : 0);
  const exchangeRate = Number(form.exchangeRate || 0);
  const euroTotal = exchangeRate > 0 ? invoiceTotal / exchangeRate : null;
  const euroReceived = form.euroReceived === "" ? null : Number(form.euroReceived);
  const changeLek = euroReceived !== null && exchangeRate > 0
    ? euroReceived * exchangeRate - invoiceTotal : null;

  // Te manual çmimi bazë shkruhet nga shitësi.
  const manualBasePrice =
    Number(form.unitPrice || 0);

  // Çmimi final për njësi.
  const calculatedUnitPrice = Math.max(0, discountBasePrice - proposedDiscountLek);

  // Vetëm Stock ka limit të vendosur te Products.
  const minimumAllowedPrice = selectedProduct
    ? getProductMinAllowedPrice(selectedProduct)
    : 0;

  const total =
    Number(form.quantity || 0) *
    Number(calculatedUnitPrice || 0);

  // Bonusi llogaritet nga fitimi pas uljes, njësoj si në backend.
  const productPurchasePrice = selectedProduct
    ? getProductPurchasePrice(selectedProduct)
    : 0;

  const productProfit = selectedProduct
    ? getProductProfit(selectedProduct)
    : 0;

  const productBonusPercent = selectedProduct
    ? getProductBonusPercent(selectedProduct)
    : 0;

  const calculatedBonus = !manualProduct && selectedProduct
    ? Math.max(
      Number(selectedProduct.min_bonus || 0),
      Math.max(Number(calculatedUnitPrice) - productPurchasePrice, 0) *
      (productBonusPercent / 100)
    ) * Number(form.quantity || 0)
    : 0;
  /*
    const handlePrintInvoice = useReactToPrint({
      contentRef: invoiceRef,
  
      documentTitle: `Fatura-Shitje-${
        selectedSale?.id || "MasterSol&Tech"
      }`,
  
     pageStyle: `
    @page {
      size: A4 portrait;
      margin: 8mm;
    }
  
    @media print {
      html,
      body {
        width: 210mm !important;
        height: 297mm !important;
        margin: 0 !important;
        padding: 0 !important;
        background: #ffffff !important;
        color: #000000 !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
  
      .sales-print-invoice {
        width: 194mm !important;
        max-width: 194mm !important;
        min-height: 0 !important;
        height: auto !important;
  
        margin: 0 !important;
        padding: 6mm !important;
  
        box-sizing: border-box !important;
  
        page-break-before: avoid !important;
        page-break-after: avoid !important;
        page-break-inside: avoid !important;
  
        break-before: avoid !important;
        break-after: avoid !important;
        break-inside: avoid !important;
      }
  
      .sales-print-invoice * {
        box-sizing: border-box !important;
      }
    }
  `,
    });
   
  
    const printSaleInvoice = (sale) => {
      setSelectedSale(sale);
  
      window.setTimeout(() => {
        handlePrintInvoice();
      }, 300);
    };
    */

  // Safe filename for the browser's Print > Save as PDF workflow.
  const warrantyFilePart = (value, fallback) =>
    String(value || fallback)
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[\\/:*?"<>|\u0000-\u001f]/g, "-")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^[-.]+|[-.]+$/g, "")
      .slice(0, 75) || fallback;

  const handlePrintWarranty = useReactToPrint({
    contentRef: warrantyRef,
    documentTitle: `Garancia-${warrantyFilePart(selectedSale?.product_name, "Produkt")}-${warrantyFilePart(selectedSale?.customer_name, "Klient")}`,
    pageStyle: `
      @page { size: A4 portrait; margin: 8mm; }
      @media print {
        html, body { margin: 0 !important; padding: 0 !important;
          background: #fff !important; -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important; }
        .sales-print-warranty { width: 194mm !important; max-width: 194mm !important;
          min-width: 0 !important; box-sizing: border-box !important;
          padding: 5mm !important; margin: 0 auto !important;
          transform: none !important; overflow: visible !important; }
        .sales-print-warranty * { box-sizing: border-box !important; }
        .sales-print-warranty header { flex-wrap: wrap !important; }
        .sales-print-warranty section { break-inside: avoid; page-break-inside: avoid; }
      }
    `,
  });

  const printWarranty = (sale) => {
    setSelectedSale(sale);
    // Allow React to commit the selected sale before Safari opens the print dialog.
    window.setTimeout(() => {
      window.requestAnimationFrame(() => handlePrintWarranty());
    }, 180);
  };

  const stopBarcodeScanner = () => {
    try {
      barcodeScannerControlsRef.current?.stop();
    } catch (error) {
      console.warn("SALE BARCODE SCANNER STOP ERROR:", error);
    }

    barcodeScannerControlsRef.current = null;

    const video = barcodeVideoRef.current;
    const stream = video?.srcObject;

    if (stream?.getTracks) {
      stream.getTracks().forEach((track) => track.stop());
    }

    if (video) {
      video.srcObject = null;
    }

    setBarcodeScannerBusy(false);
  };

  const closeBarcodeScanner = () => {
    stopBarcodeScanner();
    setBarcodeScannerOpen(false);
    setBarcodeScannerError("");
  };

  const applyScannedProduct = (product) => {
    if (!product) return;

    setManualProduct(false);

    setForm((previous) => ({
      ...previous,
      productId: product.device_purchase_id || "",
      quantity: 1,
      productName: getProductName(product),
      barcode: product.barcode || previous.barcode || "",
      unitPrice: getProductBasePrice(product),
      discountPercent: "0",
      discountLek: "0",
      imei: product.imei || "",
      warrantyMonths:
        product?.warranty_months ??
        product?.warrantyMonths ??
        previous.warrantyMonths ??
        "",
    }));
  };

  const lookupProductByBarcode = async (rawBarcode) => {
    const barcode = String(rawBarcode || "").trim();

    if (!barcode) {
      setMessage({
        type: "error",
        text: "Vendos ose skano një barcode.",
      });
      return null;
    }

    try {
      setBarcodeLookupBusy(true);
      setMessage({ type: "", text: "" });

      const response = await fetch(
        `${API_BASE_URL}/api/sales/product-by-barcode/${encodeURIComponent(barcode)}`,
        {
          headers: getAuthHeaders(),
        }
      );

      const data = await readResponse(response);

      if (handleUnauthorized(response)) return null;

      if (!response.ok) {
        throw new Error(
          data?.details ||
          data?.error ||
          data?.message ||
          "Produkti nuk u gjet me këtë barcode."
        );
      }

      const product = data?.product || data?.data || data;

      if (!product?.device_purchase_id) {
        throw new Error("Produkti i skanuar nuk është i vlefshëm.");
      }

      if (getProductStock(product) <= 0 && !(editingId &&
        String(product.device_purchase_id) === String(editingDevice?.device_purchase_id))) {
        throw new Error("Produkti u gjet, por nuk ka stok.");
      }

      setProducts((previous) => previous.some((item) => String(item.device_purchase_id) === String(product.device_purchase_id))
        ? previous.map((item) => String(item.device_purchase_id) === String(product.device_purchase_id) ? product : item)
        : [...previous, product]);
      applyScannedProduct(product);

      setMessage({
        type: "success",
        text: `U gjet ${getProductName(product)}. Stok: ${getProductStock(product)}.`,
      });

      return product;
    } catch (error) {
      setMessage({
        type: "error",
        text: error.message || "Produkti nuk u gjet me barcode.",
      });
      return null;
    } finally {
      setBarcodeLookupBusy(false);
    }
  };

  const startBarcodeScanner = async () => {
    stopBarcodeScanner();
    setBarcodeScannerError("");
    setMessage({ type: "", text: "" });

    // getUserMedia kërkon HTTPS (përveç localhost).
    if (!window.isSecureContext) {
      setBarcodeScannerOpen(true);
      setBarcodeScannerError(
        "Kamera kërkon lidhje HTTPS. Hape sistemin me https:// dhe provo përsëri."
      );
      return;
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      setBarcodeScannerOpen(true);
      setBarcodeScannerError(
        "Ky browser nuk ofron akses te kamera. Provo Chrome/Edge/Safari të përditësuar."
      );
      return;
    }

    setBarcodeScannerOpen(true);
    setBarcodeScannerBusy(true);

    try {
      // Nëse Permissions API ekziston, kontrollojmë nëse kamera është bllokuar.
      // Safari/iOS mund të mos e mbështesë query({name:'camera'}), prandaj
      // ky kontroll nuk duhet të ndalojë scanner-in nëse hedh exception.
      if (navigator.permissions?.query) {
        try {
          const permission = await navigator.permissions.query({
            name: "camera",
          });

          if (permission.state === "denied") {
            throw Object.assign(
              new Error(
                "Leja e kamerës është e bllokuar për këtë faqe. Ndrysho Camera në Allow te Site Settings dhe rifresko faqen."
              ),
              { name: "NotAllowedError" }
            );
          }
        } catch (permissionError) {
          if (permissionError?.name === "NotAllowedError") {
            throw permissionError;
          }
          // Browser-i nuk mbështet Permissions API për camera.
          // Vazhdojmë normalisht me getUserMedia/ZXing.
        }
      }

      await new Promise((resolve) => window.setTimeout(resolve, 250));

      const videoElement = barcodeVideoRef.current;

      if (!videoElement) {
        throw new Error("Elementi video i kamerës nuk u gjet.");
      }

      // playsInline është i rëndësishëm sidomos për iPhone/iPad.
      videoElement.setAttribute("playsinline", "true");
      videoElement.setAttribute("autoplay", "true");
      videoElement.muted = true;

      const codeReader = new BrowserMultiFormatReader();
      let handled = false;

      const controls = await codeReader.decodeFromConstraints(
        {
          video: {
            // Prefero kamerën e pasme në telefon.
            facingMode: { ideal: "environment" },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        },
        videoElement,
        async (result, error) => {
          if (handled) return;

          if (result) {
            handled = true;

            const barcode = String(
              result.getText?.() || result
            ).trim();

            if (!barcode) {
              handled = false;
              return;
            }

            // Vendos barcode-in e lexuar në formë.
            setForm((previous) => ({
              ...previous,
              barcode,
            }));

            // Mbyll kamerën sapo kodi lexohet.
            stopBarcodeScanner();
            setBarcodeScannerOpen(false);
            setBarcodeScannerError("");

            /*
              RRJEDHA:
              Kamera -> barcode -> /api/sales/product-by-barcode/:barcode
              -> blerja e lidhur me QR -> plotësimi automatik i shitjes.
            */
            const product = await lookupProductByBarcode(barcode);

            if (!product) {
              setMessage({
                type: "error",
                text: `Barcode ${barcode} u lexua, por nuk u gjet pajisje përkatëse te blerjet.`,
              });
            }

            return;
          }

          // NotFoundException është normale: ZXing po kërkon barcode në frame-in aktual.
          if (
            error?.name &&
            error.name !== "NotFoundException"
          ) {
            console.debug(
              "SALE BARCODE CAMERA SCAN:",
              error.name,
              error.message || ""
            );
          }
        }
      );

      barcodeScannerControlsRef.current = controls;
      setBarcodeScannerBusy(false);
    } catch (error) {
      console.error("SALE BARCODE SCANNER ERROR:", error);
      stopBarcodeScanner();

      let errorText =
        error?.message ||
        "Kamera nuk mund të hapej për skanim.";

      if (
        error?.name === "NotAllowedError" ||
        error?.name === "SecurityError"
      ) {
        errorText =
          "Aksesi te kamera është i bllokuar. Te browser-i hap Site settings > Camera > Allow, pastaj rifresko faqen.";
      } else if (
        error?.name === "NotFoundError" ||
        error?.name === "DevicesNotFoundError"
      ) {
        errorText =
          "Nuk u gjet asnjë kamerë në këtë pajisje.";
      } else if (
        error?.name === "NotReadableError" ||
        error?.name === "TrackStartError"
      ) {
        errorText =
          "Kamera nuk mund të përdoret. Mbyll aplikacionet e tjera që po përdorin kamerën dhe provo përsëri.";
      } else if (
        error?.name === "OverconstrainedError" ||
        error?.name === "ConstraintNotSatisfiedError"
      ) {
        errorText =
          "Kamera u gjet, por konfigurimi i kërkuar nuk mbështetet nga pajisja.";
      }

      setBarcodeScannerError(errorText);
      setBarcodeScannerOpen(true);
      setBarcodeScannerBusy(false);
    }
  };

  useEffect(() => {
    return () => {
      stopBarcodeScanner();
    };
  }, []);

  const openNewSaleModal = () => {
    setEditingDevice(null);
    setForm(initialForm);
    setInvoiceItems([]);
    setManualProduct(false);
    setEditingId(null);
    setMessage({
      type: "",
      text: "",
    });
    setShowModal(true);
  };

  const closeModal = () => {
    if (saving) return;

    setShowModal(false);
    setEditingDevice(null);
    setForm(initialForm);
    setInvoiceItems([]);
    setManualProduct(false);
    setEditingId(null);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setForm((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleProductChange = (event) => {
    const productId = event.target.value;

    const product = availableProducts.find(
      (item) =>
        String(item.device_purchase_id) ===
        String(productId)
    );

    setForm((previous) => ({
      ...previous,

      productId,
      quantity: 1,

      productName: product
        ? getProductName(product)
        : "",

      barcode:
        product?.barcode || "",

      // Për Stock çmimi nuk shkruhet nga shitësi.
      // Merret automatikisht nga produkti.
      unitPrice: product
        ? getProductBasePrice(product)
        : "",

      discountPercent: "0",
      discountLek: "0",

      imei:
        product?.imei || "",

      warrantyMonths:
        product?.warranty_months ??
        product?.warrantyMonths ??
        previous.warrantyMonths ??
        "",
    }));
  };

  const selectStockMode = () => {
    setManualProduct(false);

    setForm((previous) => ({
      ...previous,
      productId: "",
      productName: "",
      barcode: "",
      unitPrice: "",
      purchasePrice: "",
      discountPercent: "0",
      discountLek: "0",
      imei: "",
      warrantyMonths: "",
    }));
  };

  const selectManualMode = () => {
    setManualProduct(true);

    setForm((previous) => ({
      ...previous,
      productId: "",
      productName: "",
      barcode: "",
      unitPrice: "",
      purchasePrice: "",
      discountPercent: "0",
      discountLek: "0",
      imei: "",
      warrantyMonths: "",
    }));
  };

  const validateForm = () => {
    if (!Number.isInteger(Number(form.warrantyMonths)) || Number(form.warrantyMonths) < 0 || Number(form.warrantyMonths) > 240) {
      return "Afati i garancisë duhet të jetë nga 0 deri në 240 muaj.";
    }
    if (!form.productName.trim()) {
      return "Vendos ose zgjidh produktin.";
    }

    if (
      !Number.isInteger(
        Number(form.quantity)
      ) ||
      Number(form.quantity) < 1
    ) {
      return "Sasia duhet të jetë të paktën 1.";
    }

    const proposedDiscount = proposedDiscountPercent;
    if (form.discountLek === "" || !Number.isFinite(proposedDiscountLek) || proposedDiscountLek < 0) {
      return "Ulja në lekë duhet të jetë numër jo negativ.";
    }
    if (discountBasePrice <= 0 || proposedDiscountLek >= discountBasePrice) {
      return "Ulja duhet të jetë më e vogël se çmimi bazë i produktit.";
    }
    if (form.exchangeRate !== "" && (!Number.isFinite(exchangeRate) || exchangeRate <= 0)) {
      return "Kursi i euros duhet të jetë më i madh se zero.";
    }
    if (form.euroReceived !== "" && (exchangeRate <= 0 || !Number.isFinite(euroReceived) || euroReceived < 0)) {
      return "Për pagesë në euro vendos kursin dhe shumën e marrë.";
    }
    if (changeLek !== null && changeLek < -0.01) {
      return "Eurot e marra nuk mbulojnë totalin e shitjes.";
    }

    if (manualProduct) {
      if (form.purchasePrice !== "" && (!Number.isFinite(Number(form.purchasePrice)) || Number(form.purchasePrice) < 0)) {
        return "Çmimi i blerjes duhet të jetë 0 ose më i madh.";
      }
      if (
        !Number.isFinite(
          Number(form.unitPrice)
        ) ||
        Number(form.unitPrice) <= 0
      ) {
        return "Vendos një çmim të vlefshëm.";
      }

      if (proposedDiscount > 100) {
        return "Ulja e propozuar për produktin manual duhet të jetë nga 0% deri në 100%.";
      }
    } else {
      if (!selectedProduct) {
        return "Zgjidh pajisjen nga blerjet.";
      }

      if (Number(form.quantity) !== 1) return "Sasia e një pajisjeje duhet të jetë 1.";
      const stock = editingId && String(selectedProduct.device_purchase_id) === String(editingDevice?.device_purchase_id)
        ? 1 : getProductStock(selectedProduct);

      if (
        Number(form.quantity) > stock
      ) {
        return `Stoku aktual i produktit është ${stock}.`;
      }

    }

    return "";
  };

  const makeInvoiceItem = () => ({
    device_purchase_id: manualProduct ? null : Number(selectedProduct?.device_purchase_id),
    product_id: manualProduct ? null : Number(selectedProduct?.product_id || 0) || null,
    product_name: form.productName.trim(),
    barcode: form.barcode.trim() || null,
    quantity: Number(form.quantity),
    unit_price: Number(calculatedUnitPrice.toFixed(2)),
    original_price: manualProduct ? Number(form.unitPrice) : Number(getProductPrice(selectedProduct)),
    purchase_price: manualProduct ? Number(form.purchasePrice || 0) : getProductPurchasePrice(selectedProduct),
    discount_percent: manualProduct
      ? Number(proposedDiscountPercent)
      : Number(getProductPrice(selectedProduct)) > 0
        ? (1 - calculatedUnitPrice / getProductPrice(selectedProduct)) * 100 : 0,
    total: Number(total.toFixed(2)),
    bonus_total: manualProduct ? 0 : Number(calculatedBonus.toFixed(2)),
    imei: form.imei.trim() || null,
    warranty_months: Number(form.warrantyMonths || 0),
    is_manual: manualProduct ? 1 : 0,
  });

  const addInvoiceItem = () => {
    const error = validateForm();
    if (error) {
      setMessage({ type: "error", text: error });
      return;
    }
    const item = makeInvoiceItem();
    if (!item.is_manual && invoiceItems.some(row =>
      String(row.device_purchase_id) === String(item.device_purchase_id))) {
      setMessage({ type: "error", text: "Kjo pajisje është shtuar tashmë në faturë." });
      return;
    }
    setInvoiceItems(previous => [...previous, item]);
    setForm(previous => ({ ...previous, productId: "", productName: "", barcode: "",
      quantity: 1, unitPrice: "", purchasePrice: "", discountPercent: "0", discountLek: "0", imei: "", warrantyMonths: "0" }));
    setMessage({ type: "", text: "" });
    window.requestAnimationFrame(() => productPickerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (saving || editingDeviceBusy) return;

    setMessage({
      type: "",
      text: "",
    });

    const hasDraft = Boolean(form.productName.trim() || form.productId);
    const validationError = (invoiceItems.length === 0 || hasDraft) ? validateForm() : "";

    if (validationError) {
      setMessage({
        type: "error",
        text: validationError,
      });

      return;
    }

    try {
      setSaving(true);

      const multiInvoice = !editingId && invoiceItems.length > 0;
      const allItems = multiInvoice
        ? [...invoiceItems, ...(hasDraft ? [makeInvoiceItem()] : [])] : [];
      if (multiInvoice) {
        const ids = allItems.filter(item => !item.is_manual).map(item => String(item.device_purchase_id));
        if (new Set(ids).size !== ids.length) throw new Error("Një pajisje është shtuar dy herë.");
      }
      const response = await fetch(
        multiInvoice ? `${API_BASE_URL}/api/sales/invoice` : editingId
          ? `${API_BASE_URL}/api/sales/${editingId}`
          : `${API_BASE_URL}/api/sales`,
        {
          method: editingId ? "PUT" : "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify(multiInvoice ? {
            items: allItems,
            customer_name: form.customerName.trim() || null,
            customer_phone: form.customerPhone.trim() || null,
            customer_address: form.customerAddress.trim() || null,
            customer_email: form.customerEmail.trim() || null,
            notes: form.notes.trim() || null,
          } : {
            seller_id: loggedUser?.id || null,
            device_purchase_id: !manualProduct && selectedProduct
              ? Number(selectedProduct.device_purchase_id) : null,
            product_id: !manualProduct && selectedProduct?.product_id
              ? Number(selectedProduct.product_id) : null,
            product_name: form.productName.trim(),
            barcode: form.barcode.trim() || null,
            quantity: Number(form.quantity),
            unit_price:
              Number(calculatedUnitPrice),
            original_price: manualProduct ? Number(form.unitPrice) : undefined,
            purchase_price: manualProduct ? Number(form.purchasePrice || 0) : undefined,
            discount_percent:
              Number(
                proposedDiscountPercent
              ),
            total,

            // Parashikim; bonusi përfundimtar llogaritet në backend.
            bonus: !manualProduct
              ? Number(calculatedBonus)
              : 0,
            bonus_percent: !manualProduct
              ? Number(productBonusPercent)
              : 0,

            imei: form.imei.trim() || null,
            warranty_months: Number(form.warrantyMonths || 0),
            customer_name: form.customerName.trim() || null,
            customer_phone: form.customerPhone.trim() || null,
            customer_address: form.customerAddress.trim() || null,
            customer_email: form.customerEmail.trim() || null,
            notes: form.notes.trim() || null,
            is_manual: manualProduct ? 1 : 0,
          }),
        }
      );

      const data = await readResponse(response);

      if (handleUnauthorized(response)) return;

      if (!response.ok) {
        throw new Error(
          data?.details ||
          data?.error ||
          data?.message ||
          "Shitja nuk mund të ruhej."
        );
      }

      closeModal();

      setMessage({
        type: "success",
        text: editingId
          ? "Shitja u përditësua me sukses."
          : "Shitja u regjistrua me sukses.",
      });

      await Promise.all([
        fetchSales(),
        fetchProducts(),
      ]);
    } catch (error) {
      setMessage({
        type: "error",
        text: error.message,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (sale) => {
    if (editingDeviceBusy || saving) return;
    const [itemResponse, itemData] = await (async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/sales/${sale.id}/items`, { headers: getAuthHeaders() });
        return [response, await readResponse(response)];
      } catch { return [null, null]; }
    })();
    if (itemResponse?.ok && Array.isArray(itemData?.items) && itemData.items.length) {
      setMessage({ type: "error", text: "Faturat me disa produkte nuk mund të ndryshohen nga ky formular. Mund ta fshish dhe ta regjistrosh përsëri." });
      return;
    }
    const isManual = Number(sale.is_manual) === 1;
    if (!isManual && !sale.device_purchase_id) {
      setMessage({ type: "error", text: "Kjo shitje nuk është lidhur me blerjen. Kontrollo migrimin e të dhënave." });
      return;
    }
    let stockProduct = null;
    setEditingDeviceBusy(true);
    setMessage({ type: "", text: "" });
    try {
      if (!isManual) {
        const response = await fetch(
          `${API_BASE_URL}/api/sales/device/${encodeURIComponent(sale.device_purchase_id)}`,
          { headers: getAuthHeaders() }
        );
        const data = await readResponse(response);
        if (handleUnauthorized(response)) return;
        if (!response.ok) throw new Error(data.error || "Pajisja nuk mund të ngarkohej.");
        stockProduct = data.device_purchase;
        if (!stockProduct?.device_purchase_id) throw new Error("Blerja e lidhur mungon.");
      }
      setEditingId(sale.id);
      setEditingDevice(stockProduct);
      setManualProduct(isManual);
      setForm({
        productId:
          sale.device_purchase_id || "",

        productName:
          sale.product_name || "",

        barcode:
          sale.barcode ||
          stockProduct?.barcode ||
          "",

        quantity:
          sale.quantity || 1,

        // Për manual original_price është çmimi para uljes.
        // Për pajisjen merret çmimi aktual nga blerja.
        unitPrice: isManual
          ? (
            Number(sale.original_price) > 0
              ? sale.original_price
              : sale.unit_price || ""
          )
          : (
            stockProduct
              ? getProductBasePrice(stockProduct)
              : sale.unit_price || ""
          ),

        purchasePrice: isManual ? String(sale.purchase_price ?? 0) : "",
        discountPercent: String(Number(sale.discount_percent || 0)),
        discountLek: String(Math.max(0, (isManual ? Number(sale.original_price || sale.unit_price || 0) : getProductBasePrice(stockProduct)) * Number(sale.discount_percent || 0) / 100)),
        exchangeRate: "",
        euroReceived: "",

        imei:
          sale.imei || "",

        warrantyMonths:
          sale.warranty_months ?? "",

        customerName:
          sale.customer_name || "",

        customerPhone:
          sale.customer_phone || "",

        customerAddress:
          sale.customer_address || "",

        customerEmail:
          sale.customer_email || "",

        notes:
          sale.notes || "",
      });

      setShowModal(true);
    } catch (error) {
      setMessage({ type: "error", text: error.message || "Shitja nuk mund të hapej për editim." });
    } finally {
      setEditingDeviceBusy(false);
    }
  };

  const handleDelete = async (saleId) => {
    const confirmed = window.confirm(
      "A je e sigurt që do ta fshish këtë shitje?"
    );

    if (!confirmed) return;

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/sales/${saleId}`,
        {
          method: "DELETE",
          headers: getAuthHeaders(),
        }
      );

      const data = await readResponse(response);

      if (handleUnauthorized(response)) return;

      if (!response.ok) {
        throw new Error(
          data?.details ||
          data?.error ||
          data?.message ||
          "Shitja nuk mund të fshihej."
        );
      }

      setMessage({
        type: "success",
        text: "Shitja u fshi me sukses.",
      });

      await Promise.all([
        fetchSales(),
        fetchProducts(),
      ]);
    } catch (error) {
      setMessage({
        type: "error",
        text: error.message,
      });
    }
  };

  return (
    <div className="sales-design min-h-screen overflow-x-hidden bg-gray-100 text-gray-800">
      <style>{salesDesign}</style>
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() =>
          setSidebarOpen(false)
        }
      />

      <button
        type="button"
        onClick={() =>
          setSidebarOpen(true)
        }
        className="
             fixed left-4 top-4 z-30
             flex h-11 w-11
             items-center justify-center
             rounded-xl bg-blue-700
             text-white shadow-lg
             transition hover:bg-blue-800
             active:scale-95
             lg:hidden
           "
        aria-label="Hap menunë"
      >
        <Menu size={24} />
      </button>

      <main
        className="
          min-h-screen
          w-full
          min-w-0
          px-3
          pb-8
          pt-20
          sm:px-6
          lg:ml-64
          lg:w-[calc(100%-16rem)]
          lg:p-8
        "
      >
        {/* HEADER */}

        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-blue-600">
              Regjistri i dyqanit
            </span>

            <h1 className="mt-1 text-3xl font-bold text-gray-900">Shitjet</h1>

            <p>
              Shiko dhe menaxho të gjitha shitjet e regjistruara.
            </p>
          </div>

          <button
            type="button"
            className="sales-new-button"
            onClick={openNewSaleModal}
          >
            <Plus size={19} />
            Shitje e re
          </button>
        </div>

        {message.text && (
          <div
            className={`sales-message sales-message-${message.type}`}
          >
            {message.text}
          </div>
        )}

        <section className="sales-filter-panel">
          <div className="sales-list-toolbar">
            <div>
              <h2>Të gjitha shitjet</h2>
              <p>
                {sales.length} shitje të regjistruara
              </p>
            </div>

            <div className="sales-search">
              <Search size={18} />

              <input
                type="text"
                value={search}
                onChange={(event) =>
                  setSearch(event.target.value)
                }
                placeholder="Kërko produkt ose shitës..."
              />
            </div>
          </div>

        </section>

        <section className="sales-list-card">
          {loadingSales ? (
            <div className="sales-empty">
              Duke ngarkuar shitjet...
            </div>
          ) : filteredSales.length === 0 ? (
            <div className="sales-empty">
              <ShoppingBag size={40} />

              <h3>Nuk ka shitje të regjistruara</h3>

              <p>
                Kliko “Shitje e re” për të regjistruar shitjen
                e parë.
              </p>
            </div>
          ) : (
            <div className="sales-table-wrapper w-full max-w-none overflow-x-auto">
              <table className="sales-table">
                <thead>
                  <tr>
                    <th>Nr.</th>
                    <th>Produkti</th>
                    <th>Sasia</th>
                    <th>Çmimi</th>
                    <th>Totali</th>
                    <th>Bonusi</th>
                    <th>Klienti</th>
                    <th>Shitësi</th>
                    <th>Data</th>
                    <th>Veprime</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredSales.map((sale, index) => (
                    <tr
                      key={sale.id}
                      tabIndex={0}
                      className="cursor-pointer"
                      onClick={(event) => {
                        if (event.target.closest("button, a, input, select, textarea")) return;
                        setMobileDetailSale(sale);
                      }}
                      onKeyDown={(event) => {
                        if (event.target !== event.currentTarget) return;
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setMobileDetailSale(sale);
                        }
                      }}
                    >
                      <td>#{sale.id || index + 1}</td>

                      <td>
                        <button
                          type="button"
                          className="sales-name-link"
                          onClick={() => setMobileDetailSale(sale)}
                          title="Shiko detajet e shitjes"
                        >
                          {sale.product_name || "Produkt"}
                        </button>
                      </td>

                      <td>{sale.quantity}</td>

                      <td>
                        {formatPrice(sale.unit_price)} ALL
                      </td>

                      <td className="sales-total">
                        {formatPrice(sale.total)} ALL
                      </td>

                      <td>
                        {Number(sale.is_manual) === 1 ? "—" : `${formatPrice(sale.bonus_total)} ALL`}
                      </td>

                      <td>
                        <strong>{sale.customer_name || "—"}</strong>
                        {sale.customer_phone && (
                          <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "2px" }}>
                            {sale.customer_phone}
                          </div>
                        )}
                      </td>

                      <td>
                        {sale.seller_name ||
                          sale.username ||
                          "—"}
                      </td>

                      <td>
                        {sale.created_at
                          ? new Date(
                            sale.created_at
                          ).toLocaleString("sq-AL")
                          : "—"}
                      </td>

                      <td>
                        <div className="sales-actions">
                          <button
                            type="button"
                            className="sales-view"
                            onClick={() => setMobileDetailSale(sale)}
                            title="Shiko detajet"
                            aria-label="Shiko detajet e shitjes"
                          >
                            <Eye size={17} />
                          </button>



                          <button
                            type="button"
                            className="flex h-9 w-9 items-center justify-center rounded-lg bg-violet-50 text-violet-600 hover:bg-violet-100 disabled:cursor-not-allowed disabled:opacity-30"
                            onClick={() => printWarranty(sale)}
                            title="Printo fletën e garancisë"
                          >
                            <ShieldCheck size={17} />
                          </button>

                          <button
                            type="button"
                            className="sales-edit"
                            onClick={() => handleEdit(sale)}
                            title="Ndrysho"
                          >
                            <Edit3 size={16} />
                          </button>

                          <button
                            type="button"
                            className="sales-delete"
                            onClick={() =>
                              handleDelete(sale.id)
                            }
                            title="Fshi"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>


        {/* MOBILE VERSION */}
        <div className="sales-mobile-cards">
          {filteredSales.map((sale, index) => (
            <button
              key={sale.id}
              type="button"
              className="sales-mobile-card"
              aria-label={`Shiko detajet: ${sale.product_name || "Produkt"}`}
              onClick={() => setMobileDetailSale(sale)}
            >
              <div className="sales-mobile-card-top">
                <span>#{sale.id || index + 1}</span>

                <span
                  className={`sales-type-badge ${sale.is_manual ? "manual" : "stock"
                    }`}
                >
                  {sale.is_manual ? "Manual" : "Nga stoku"}
                </span>
              </div>

              <h3 className="sales-mobile-product-name">
                {sale.product_name || "Produkt"}
              </h3>

              <div className="sales-mobile-info">
                <div>
                  <span>Sasia</span>
                  <strong>{sale.quantity}</strong>
                </div>

                <div>
                  <span>Shitësi</span>
                  <strong>
                    {sale.seller_name || sale.username || "—"}
                  </strong>
                </div>
              </div>

              <div className="sales-mobile-footer">
                <div>
                  <span>Totali</span>
                  <strong>{formatPrice(sale.total)} ALL</strong>
                </div>

                <div className="sales-mobile-details-link">
                  Shiko detajet
                  <Eye size={17} />
                </div>
              </div>
            </button>
          ))}
        </div>

        {mobileDetailSale && (
          <div
            className="sales-detail-overlay"
            onClick={() => setMobileDetailSale(null)}
          >
            <div
              className="sales-detail-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sales-detail-header">
                <div>
                  <span>Detajet e shitjes</span>

                  <h2>
                    {mobileDetailSale.product_name || "Produkt"}
                  </h2>
                </div>

                <button
                  type="button"
                  onClick={() => setMobileDetailSale(null)}
                >
                  ×
                </button>
              </div>

              {detailItems.length > 0 && (
                <div className="rounded-xl border border-blue-100 bg-blue-50/40 p-3">
                  <h3 className="mb-2 font-bold">Produktet në faturë</h3>
                  {detailItems.map(item => (
                    <div key={item.id} className="flex justify-between gap-3 border-b py-2 text-sm">
                      <span>{item.product_name} · {item.quantity} × {formatPrice(item.unit_price)} ALL</span>
                      <strong>{formatPrice(item.total)} ALL</strong>
                    </div>
                  ))}
                </div>
              )}
              <div className="sales-detail-grid">
                <div>
                  <span>Tipi i shitjes</span>
                  <strong>{Number(mobileDetailSale.is_manual) === 1 ? "Manual" : "Nga stoku"}</strong>
                </div>
                <div>
                  <span>Kategoria e shitjes</span>
                  <strong>{({shop: "Në dyqan", repair: "Servis", online: "Online"})[mobileDetailSale.sale_type] || mobileDetailSale.sale_type || "—"}</strong>
                </div>
                <div>
                  <span>Brand / Model</span>
                  <strong>{[mobileDetailSale.product_brand, mobileDetailSale.product_model].filter(Boolean).join(" · ") || "—"}</strong>
                </div>
                <div>
                  <span>Çmimi fillestar</span>
                  <strong>{mobileDetailSale.original_price == null ? "—" : `${formatPrice(mobileDetailSale.original_price)} ALL`}</strong>
                </div>
                <div>
                  <span>Zbritja</span>
                  <strong>{formatPrice(mobileDetailSale.discount_percent)}%</strong>
                </div>
                <div className="sales-detail-notes">
                  <span>Shënime</span>
                  <strong>{mobileDetailSale.notes || "—"}</strong>
                </div>

                <div>
                  <span>Nr.</span>
                  <strong>#{mobileDetailSale.id}</strong>
                </div>

                <div>
                  <span>Barcode</span>
                  <strong className="font-mono">
                    {mobileDetailSale.barcode || "—"}
                  </strong>
                </div>

                <div>
                  <span>Sasia</span>
                  <strong>{mobileDetailSale.quantity}</strong>
                </div>

                <div>
                  <span>Çmimi</span>
                  <strong>
                    {formatPrice(mobileDetailSale.unit_price)} ALL
                  </strong>
                </div>

                <div>
                  <span>Totali</span>
                  <strong className="detail-total">
                    {formatPrice(mobileDetailSale.total)} ALL
                  </strong>
                </div>

                <div>
                  <span>Bonusi</span>
                  <strong>
                    {Number(mobileDetailSale.is_manual) === 1 ? "—" : `${formatPrice(mobileDetailSale.bonus_total)} ALL`}
                  </strong>
                </div>

                <div>
                  <span>Klienti</span>
                  <strong>
                    {mobileDetailSale.customer_name || "—"}
                  </strong>
                </div>

                <div>
                  <span>Telefon</span>
                  <strong>
                    {mobileDetailSale.customer_phone || "—"}
                  </strong>
                </div>

                <div>
                  <span>Email</span>
                  <strong>
                    {mobileDetailSale.customer_email || "—"}
                  </strong>
                </div>

                <div>
                  <span>Adresa</span>
                  <strong>
                    {mobileDetailSale.customer_address || "—"}
                  </strong>
                </div>

                <div>
                  <span>Shitësi</span>
                  <strong>
                    {mobileDetailSale.seller_name ||
                      mobileDetailSale.username ||
                      "—"}
                  </strong>
                </div>

                <div>
                  <span>IMEI / Serial</span>
                  <strong>
                    {mobileDetailSale.imei ||
                      mobileDetailSale.serial_number ||
                      "—"}
                  </strong>
                </div>

                <div>
                  <span>Garancia</span>
                  <strong>
                    {Number(mobileDetailSale.warranty_months || 0) > 0
                      ? `${mobileDetailSale.warranty_months} muaj`
                      : "0 muaj / Pa garanci"}
                  </strong>
                </div>

                <div>
                  <span>Data</span>
                  <strong>
                    {mobileDetailSale.created_at
                      ? new Date(
                        mobileDetailSale.created_at
                      ).toLocaleString("sq-AL")
                      : "—"}
                  </strong>
                </div>
              </div>

              {/* VEPRIMET */}
              <div className="sales-detail-actions">


                <button
                  className="warranty"
                  onClick={() =>
                    printWarranty(mobileDetailSale)
                  }
                >
                  <ShieldCheck size={18} />
                  Garancia
                </button>

                <button
                  className="edit"
                  onClick={() => {
                    const sale = mobileDetailSale;

                    setMobileDetailSale(null);
                    handleEdit(sale);
                  }}
                >
                  <Edit3 size={18} />
                  Ndrysho
                </button>

                <button
                  className="delete"
                  onClick={() => {
                    const id = mobileDetailSale.id;

                    setMobileDetailSale(null);
                    handleDelete(id);
                  }}
                >
                  <Trash2 size={18} />
                  Fshi
                </button>
              </div>
            </div>
          </div>
        )}

        {customerHistory && (
          <div className="fixed inset-0 z-[12000] flex items-center justify-center bg-black/60 p-3"
            onMouseDown={() => setCustomerHistory(null)}>
            <div className="max-h-[85dvh] w-full max-w-xl overflow-y-auto rounded-2xl bg-white p-5 shadow-xl"
              onMouseDown={event => event.stopPropagation()}>
              <div className="mb-4 flex items-center justify-between gap-3">
                <div><h3 className="text-lg font-bold">Historiku i klientit</h3>
                  <p className="text-sm">{customerHistory.fullName} · {customerHistory.status}</p></div>
                <button type="button" onClick={() => setCustomerHistory(null)} aria-label="Mbyll"><X size={22}/></button>
              </div>
              <p className="mb-3 text-sm text-gray-500">Shitjet e ngarkuara në këtë faqe: {customerHistory.customerSales.length}</p>
              {customerHistory.customerSales.length ? customerHistory.customerSales.map(sale => (
                <div key={sale.id} className="border-b py-3 text-sm">
                  <strong>{sale.product_name || "Produkt"}</strong>
                  <div>{formatPrice(sale.total)} ALL · {sale.created_at ? new Date(sale.created_at).toLocaleDateString("sq-AL") : "Pa datë"}</div>
                </div>
              )) : <p className="text-sm text-gray-500">Nuk ka shitje në listën e ngarkuar. Për historik të plotë duhet endpoint-i i klientit.</p>}
            </div>
          </div>
        )}

        {barcodeScannerOpen && (
          <div className="fixed inset-0 z-[11000] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
            <div className="w-full max-w-xl overflow-hidden rounded-3xl bg-white shadow-2xl">
              <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
                <div>
                  <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                    <ScanLine size={20} className="text-blue-600" />
                    Skano barcode-in e produktit
                  </h3>
                  <p className="mt-1 text-xs text-gray-500">
                    Produkti do të kërkohet automatikisht në inventar.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={closeBarcodeScanner}
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-600"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="p-4">
                <div className="overflow-hidden rounded-2xl bg-black">
                  <video
                    ref={barcodeVideoRef}
                    autoPlay
                    muted
                    playsInline
                    className="aspect-video w-full object-cover"
                  />
                </div>

                {barcodeScannerBusy &&
                  !barcodeScannerError && (
                    <p className="mt-3 text-center text-sm font-medium text-gray-600">
                      Duke skanuar...
                    </p>
                  )}

                {barcodeScannerError && (
                  <div className="mt-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
                    {barcodeScannerError}
                  </div>
                )}

                <button
                  type="button"
                  onClick={closeBarcodeScanner}
                  className="mt-4 w-full rounded-xl border border-gray-300 px-4 py-3 font-semibold text-gray-700"
                >
                  Mbyll
                </button>
              </div>
            </div>
          </div>
        )}

        {showModal && (
          <div
            className="sales-modal-overlay"
            onMouseDown={closeModal}
          >
            <div
              className="sales-modal sale-entry-modal"
              onMouseDown={(event) => event.stopPropagation()}
            >
              <div className="sales-modal-header">
                <div>
                  <span className="sales-modal-eyebrow">
                    {editingId
                      ? "Ndrysho regjistrimin"
                      : "Regjistrim i ri"}
                  </span>

                  <h2>
                    {editingId
                      ? "Përditëso shitjen"
                      : "Shitje e re"}
                  </h2>
                </div>

                <button
                  type="button"
                  className="sales-modal-close"
                  onClick={closeModal}
                >
                  <X size={20} />
                </button>
              </div>

              {message.type === "error" && message.text && (
                <div className="sales-message sales-message-error">
                  {message.text}
                </div>
              )}

              <form
                className="sales-form"
                onSubmit={handleSubmit}
              >
                {!editingId && (
                  <div className="sale-quick-guide" aria-label="Hapat e shitjes">
                    <span className="sale-quick-guide-number">1</span>
                    <div><strong>Zgjidh produktin</strong><p>Pastaj shtype “Shto në faturë”. Përsërite për produktet e tjera.</p></div>
                    <span className="sale-quick-guide-count">{invoiceItems.length} në faturë</span>
                  </div>
                )}

                <div ref={productPickerRef} className="sales-mode-switch">
                  <button
                    type="button"
                    className={!manualProduct ? "active" : ""}
                    onClick={selectStockMode}
                  >
                    <Package size={18} />
                    Produkt nga stoku
                  </button>

                  <button
                    type="button"
                    className={manualProduct ? "active" : ""}
                    onClick={selectManualMode}
                  >
                    <Edit3 size={18} />
                    Produkt manual
                  </button>
                </div>

                {!manualProduct && (
                  <div className="sales-field">
                    <label htmlFor="saleBarcode">
                      Barcode
                    </label>

                    <div className="flex flex-col gap-2 sm:flex-row">
                      <div className="relative flex-1">
                        <Barcode
                          size={18}
                          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                        />

                        <input
                          id="saleBarcode"
                          name="barcode"
                          type="text"
                          value={form.barcode}
                          onChange={handleInputChange}
                          onKeyDown={(event) => {
                            if (event.key === "Enter") {
                              event.preventDefault();
                              lookupProductByBarcode(form.barcode);
                            }
                          }}
                          placeholder="Skano ose shkruaj barcode-in"
                          autoComplete="off"
                          className="w-full rounded-xl border border-gray-300 py-3 pl-10 pr-3 outline-none focus:border-blue-500"
                        />
                      </div>

                      <button
                        type="button"
                        onClick={() =>
                          lookupProductByBarcode(form.barcode)
                        }
                        disabled={
                          barcodeLookupBusy ||
                          !form.barcode.trim()
                        }
                        className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 font-semibold text-blue-700 disabled:opacity-50"
                      >
                        {barcodeLookupBusy
                          ? "Duke kërkuar..."
                          : "Gjej"}
                      </button>

                      <button
                        type="button"
                        onClick={startBarcodeScanner}
                        className="flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-3 font-semibold text-white hover:bg-blue-700"
                      >
                        <Camera size={18} />
                        Skano
                      </button>
                    </div>

                    <p className="mt-1 text-xs text-gray-500">
                      Scanner USB/Bluetooth mund të shkruajë direkt në fushë dhe Enter e kërkon produktin.
                    </p>
                  </div>
                )}

                {!manualProduct ? (
                  <div className="sales-field">
                    <label htmlFor="productId">
                      Produkti
                    </label>

                    <select
                      id="productId"
                      value={form.productId}
                      onChange={handleProductChange}
                      disabled={loadingProducts || editingDeviceBusy}
                    >
                      <option value="">
                        Zgjidh pajisjen
                      </option>

                      {availableProducts.filter(product => !invoiceItems.some(item =>
                        !item.is_manual && String(item.device_purchase_id) === String(product.device_purchase_id))).map((product) => (
                        <option
                          key={product.device_purchase_id}
                          value={product.device_purchase_id}
                        >
                          {getProductName(product)} — Stok:{" "}
                          {getProductStock(product)}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div className="sales-field">
                    <label htmlFor="productName">
                      Emri i produktit
                    </label>

                    <input
                      id="productName"
                      name="productName"
                      type="text"
                      value={form.productName}
                      onChange={handleInputChange}
                      placeholder="P.sh. Karikues USB-C"
                      autoFocus
                    />
                  </div>
                )}

                <div className="sales-form-grid">
                  <div className="sales-field">
                    <label htmlFor="imei">
                      Numri serial / IMEI
                    </label>

                    <input
                      id="imei"
                      name="imei"
                      readOnly={!manualProduct}
                      type="text"
                      value={form.imei}
                      onChange={handleInputChange}
                      placeholder="IMEI / Serial"
                    />
                  </div>

                  <div className="sales-field">
                    <label htmlFor="warrantyMonths">
                      Afati i garancisë
                    </label>

                    <div className="flex flex-col gap-2 sm:flex-row">
                      <select id="warrantyMonths" name="warrantyMonths"
                        value={form.warrantyMonths} onChange={handleInputChange}
                        className="min-w-0 flex-1">
                        {warrantyOptions.map(months => (
                          <option key={months} value={months}>
                            {months === 0 ? "Pa garanci" : `${months} muaj`}
                          </option>
                        ))}
                      </select>
                      <button type="button" onClick={() => setShowAddWarranty(v => !v)}
                        className="rounded-xl border border-blue-200 px-3 py-2 font-semibold text-blue-700">
                        + Afat i ri
                      </button>
                    </div>
                    {showAddWarranty && (
                      <div className="mt-2 flex gap-2">
                        <input type="number" inputMode="numeric" min="1" max="240" step="1"
                          value={newWarrantyTerm} onChange={e => setNewWarrantyTerm(e.target.value)}
                          placeholder="Muaj (p.sh. 48)" aria-label="Afat i ri në muaj" />
                        <button type="button" onClick={addWarrantyTerm}
                          className="rounded-xl bg-blue-600 px-4 py-2 text-white">Shto</button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="sales-form-grid">
                  <div className="sales-field">
                    <label htmlFor="quantity">
                      Sasia
                    </label>

                    <input
                      id="quantity"
                      name="quantity"
                      readOnly={!manualProduct}
                      max={!manualProduct ? 1 : undefined}
                      type="number"
                      min="1"
                      step="1"
                      value={form.quantity}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="sales-field">
                    <label htmlFor="unitPrice">
                      {manualProduct
                        ? "Çmimi për njësi"
                        : "Çmimi nga sistemi"}
                    </label>

                    <div className="sales-input-suffix">
                      <input
                        id="unitPrice"
                        name="unitPrice"
                        type="number"
                        min="0"
                        step="0.01"
                        value={
                          manualProduct
                            ? form.unitPrice
                            : selectedProduct?.price ?? ""
                        }
                        onChange={
                          manualProduct
                            ? handleInputChange
                            : undefined
                        }
                        readOnly={!manualProduct}
                        placeholder="0"
                      />

                      <span>ALL</span>
                    </div>
                  </div>
                </div>

                {manualProduct && (
                  <div className="sales-field">
                    <label htmlFor="purchasePrice">Çmimi i blerjes (ALL)</label>
                    <div className="sales-input-suffix">
                      <input id="purchasePrice" name="purchasePrice" type="number"
                        min="0" step="0.01" inputMode="decimal"
                        value={form.purchasePrice} onChange={handleInputChange}
                        placeholder="0" />
                      <span>ALL</span>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Nëse nuk ka çmim blerjeje, lëre bosh (llogaritet 0 ALL).
                    </p>
                  </div>
                )}

                <div className="sales-field">
                  <label htmlFor="discountLek">Ulja e propozuar për klientin (ALL)</label>
                  <div className="sales-input-suffix">
                    <input id="discountLek" name="discountLek" type="number" min="0"
                      max={discountBasePrice > 0 ? discountBasePrice : undefined}
                      step="0.01" inputMode="decimal" value={form.discountLek}
                      onChange={handleInputChange} disabled={!manualProduct && !selectedProduct}
                      placeholder="0" />
                    <span>ALL</span>
                  </div>
                  <p className="mt-2 text-sm text-blue-700">
                    Ulja e llogaritur: <strong>{Number.isFinite(proposedDiscountPercent) ? proposedDiscountPercent.toFixed(2) : "0.00"}%</strong>
                    {discountBasePrice > 0 && <> · {formatPrice(proposedDiscountLek)} ALL nga {formatPrice(discountBasePrice)} ALL</>}
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    Ulja vendoset në lekë; përqindja llogaritet automatikisht.
                  </p>
                </div>

                {!editingId && (
                  <div className="sale-cart-panel">
                    <div className="sale-cart-heading">
                      <div><strong>{form.productName.trim() ? "Produkti është gati" : "Fatura jote"}</strong>
                        <p>{form.productName.trim() ? `Shto “${form.productName}” në faturë.` : "Zgjidh produktin sipër për ta shtuar."}</p></div>
                      <span>{invoiceItems.length} produkte</span>
                    </div>
                    <button type="button" onClick={addInvoiceItem} disabled={!form.productName.trim()}
                      className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-4 text-base font-extrabold text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300">
                      <Plus size={22} /> {invoiceItems.length === 0 ? "Shto në faturë" : "Shto produktin në faturë"}
                    </button>
                    <div className="mt-5 flex items-center justify-between gap-3 border-b pb-3">
                      <h3 className="font-extrabold text-gray-900">Produktet e faturës ({invoiceItems.length})</h3>
                      <span className="text-xs text-gray-500">Mund t’i heqësh para regjistrimit</span>
                    </div>
                    {invoiceItems.length === 0 ? (
                      <p className="rounded-lg bg-gray-50 p-4 text-center text-sm text-gray-600">Ende nuk ke shtuar asnjë produkt. Përdor butonin blu sipër.</p>
                    ) : (
                      <div className="mt-3 space-y-2">
                        {invoiceItems.map((item, index) => (
                          <div key={index} className="flex items-center justify-between gap-3 rounded-lg border bg-gray-50 p-3">
                            <div className="min-w-0">
                              <strong className="block">{index + 1}. {item.product_name}</strong>
                              <span className="text-xs text-gray-500">{item.quantity} × {formatPrice(item.unit_price)} ALL · {item.is_manual ? "Manual" : "Nga stoku"}</span>
                            </div>
                            <strong className="whitespace-nowrap">{formatPrice(item.total)} ALL</strong>
                            <button type="button" aria-label={`Hiq ${item.product_name} nga fatura`} title="Hiq nga fatura"
                              onClick={() => setInvoiceItems(previous => previous.filter((_, i) => i !== index))}
                              className="rounded-lg p-2 text-red-600 hover:bg-red-50"><Trash2 size={17} /></button>
                          </div>
                        ))}
                      </div>
                    )}
                    {form.productName.trim() && invoiceItems.length > 0 && (
                      <p className="mt-3 text-sm font-medium text-amber-700">Kujdes: “{form.productName}” është ende në formular. Kliko “Shto produktin tjetër në faturë” që të shfaqet në listë.</p>
                    )}
                    <div className="mt-4 flex flex-wrap items-center justify-between gap-2 rounded-xl bg-emerald-50 p-4">
                      <span className="font-semibold text-emerald-900">Totali për t’u paguar</span>
                      <strong className="text-xl text-emerald-800">{formatPrice(invoiceTotal)} ALL</strong>
                    </div>

                  </div>
                )}

                <details className="sale-optional-section" open={undefined}>
                  <summary><span><strong>Klienti</strong><small>Opsionale · emër, telefon, adresë</small></span><span className="sale-optional-chevron">⌄</span></summary>
                  <div className="sale-optional-content">
                <div className="sales-field relative">
                  <label htmlFor="customerName">Klienti (kërko në regjistër)</label>
                  <input id="customerName" name="customerName" type="text"
                    value={form.customerName}
                    onChange={event => { handleInputChange(event); setCustomerSearchOpen(true); }}
                    onFocus={() => setCustomerSearchOpen(true)}
                    placeholder="Kërko emrin ose numrin e telefonit"
                    autoComplete="off" role="combobox" aria-expanded={customerSearchOpen}
                    aria-controls="sales-customer-options" />
                  {customerSearchOpen && (
                    <div id="sales-customer-options" role="listbox"
                      className="mt-2 max-h-64 overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-lg">
                      {customerLoading && <p className="p-3 text-sm text-gray-500">Duke kërkuar klientët...</p>}
                      {customerError && <p className="p-3 text-sm text-red-600">{customerError}</p>}
                      {!customerLoading && !customerError && visibleCustomers.length === 0 &&
                        <p className="p-3 text-sm text-gray-500">Nuk u gjet klient. Mund ta regjistrosh manualisht.</p>}
                      {visibleCustomers.map((customer, index) => (
                        <div key={customer.id || `${customer.phone}-${index}`}
                          className="flex flex-wrap items-center gap-2 border-b border-gray-100 p-2">
                          <button type="button" role="option" aria-selected={false}
                            className="min-w-0 flex-1 text-left"
                            onClick={() => chooseCustomer(customer)}>
                            <span className="block font-semibold">{customer.fullName || customer.phone}</span>
                            <span className="block text-xs text-gray-500">{customer.phone || "Pa telefon"} · {customer.status}</span>
                          </button>
                          <button type="button" className="rounded-lg border px-2 py-2 text-xs font-semibold text-blue-700"
                            onClick={() => { chooseCustomer(customer); setCustomerHistory(customer); }}>
                            Historiku
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="sales-form-grid">
                  <div className="sales-field">
                    <label htmlFor="customerPhone">Nr. telefoni</label>
                    <input
                      id="customerPhone"
                      name="customerPhone"
                      type="tel"
                      value={form.customerPhone}
                      onChange={handleInputChange}
                      placeholder="P.sh. 069 123 4567"
                    />
                  </div>

                  <div className="sales-field">
                    <label htmlFor="customerEmail">Email</label>
                    <input
                      id="customerEmail"
                      name="customerEmail"
                      type="email"
                      value={form.customerEmail}
                      onChange={handleInputChange}
                      placeholder="email@example.com"
                    />
                  </div>
                </div>

                <div className="sales-field">
                  <label htmlFor="customerAddress">Adresa e klientit</label>
                  <input
                    id="customerAddress"
                    name="customerAddress"
                    type="text"
                    value={form.customerAddress}
                    onChange={handleInputChange}
                    placeholder="Adresa e klientit"
                  />
                </div>

                  </div>
                </details>

                <details className="sale-optional-section">
                  <summary><span><strong>Pagesa në euro</strong><small>Opsionale · kursi dhe kusuri</small></span><span className="sale-optional-chevron">⌄</span></summary>
                  <div className="sale-optional-content">
                <div className="rounded-2xl border border-emerald-100 bg-emerald-50/50 p-4">
                  <h3 className="mb-3 text-sm font-bold text-gray-800">Konvertimi EUR / ALL (opsional)</h3>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="sales-field">
                      <label htmlFor="exchangeRate">Kursi i ditës: 1 EUR = sa ALL?</label>
                      <input id="exchangeRate" name="exchangeRate" type="number" min="0.0001"
                        step="any" inputMode="decimal" value={form.exchangeRate}
                        onChange={handleInputChange} placeholder="P.sh. 95" />
                    </div>
                    <div className="sales-field">
                      <label htmlFor="euroReceived">Euro të marra nga klienti (opsionale)</label>
                      <input id="euroReceived" name="euroReceived" type="number" min="0"
                        step="0.01" inputMode="decimal" value={form.euroReceived}
                        onChange={handleInputChange} placeholder="P.sh. 500" />
                    </div>
                  </div>
                  {euroTotal !== null && (
                    <div className="mt-3 text-sm text-emerald-900">
                      Totali: <strong>{formatPrice(invoiceTotal)} ALL = {euroTotal.toFixed(2)} EUR</strong>
                      {changeLek !== null && (
                        <p className={changeLek < -0.01 ? "mt-1 text-red-600" : "mt-1 text-emerald-700"}>
                          {changeLek < -0.01 ? "Mungojnë" : "Kusuri"}: <strong>{formatPrice(Math.abs(changeLek))} ALL</strong>
                          {changeLek >= 0 && ` (${(changeLek / exchangeRate).toFixed(2)} EUR)`}
                        </p>
                      )}
                    </div>
                  )}
                  <p className="mt-2 text-xs text-gray-500">Kursin e vendos shitësi. Shitja ruhet në ALL; konvertimi është informues.</p>
                </div>

                  </div>
                </details>

                <details className="sale-optional-section sale-advanced">
                  <summary><span><strong>Detaje çmimi dhe bonusi</strong><small>Shfaq llogaritjet e plota</small></span><span className="sale-optional-chevron">⌄</span></summary>
                  <div className="sale-optional-content">
                {manualProduct &&
                  Number(form.unitPrice || 0) >
                  0 && (
                    <div className="rounded-2xl border border-blue-100 bg-blue-50/60 p-4">
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                        <div>
                          <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                            Çmimi manual
                          </span>

                          <strong className="mt-1 block text-base text-gray-900">
                            {formatPrice(
                              manualBasePrice
                            )}{" "}
                            ALL
                          </strong>
                        </div>

                        <div>
                          <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                            Ulja e propozuar
                          </span>

                          <strong className="mt-1 block text-base text-blue-700">
                            {formatPrice(proposedDiscountLek)} ALL ({proposedDiscountPercent.toFixed(2)}%)
                          </strong>
                        </div>

                        <div>
                          <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                            Çmimi final
                          </span>

                          <strong className="mt-1 block text-base text-emerald-700">
                            {formatPrice(
                              calculatedUnitPrice
                            )}{" "}
                            ALL
                          </strong>
                        </div>
                      </div>
                    </div>
                  )}

                {!manualProduct &&
                  selectedProduct && (
                    <div className="rounded-2xl border border-blue-100 bg-blue-50/60 p-4">
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-6">
                        {/* ÇMIMI NORMAL - DIREKT NGA PRODUCT.PRICE */}
                        <div>
                          <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                            Çmimi normal
                          </span>

                          <strong className="mt-1 block text-base text-gray-900">
                            {formatPrice(
                              productNormalPrice
                            )}{" "}
                            ALL
                          </strong>
                        </div>

                        {/* OFERTA AUTOMATIKE E PRODUKTIT */}
                        <div>
                          <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                            Ulja automatike / ofertë
                          </span>

                          <strong className="mt-1 block text-base text-orange-600">
                            {formatPrice(
                              automaticOfferPercent
                            )}
                            %
                          </strong>

                          <span className="mt-1 block text-xs text-gray-500">
                            {automaticOfferPercent > 0
                              ? `Çmimi pas ofertës: ${formatPrice(
                                stockBasePrice
                              )} ALL`
                              : `Pa ofertë — ${formatPrice(
                                stockBasePrice
                              )} ALL`}
                          </span>
                        </div>

                        {/* LIMITI I SHITËSIT */}
                        <div>
                          <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                            Ulja e propozuar
                          </span>

                          <strong className="mt-1 block text-base text-blue-700">
                            {formatPrice(proposedDiscountLek)} ALL
                          </strong>
                        </div>

                        {/* ÇMIMI MINIMAL */}
                        <div>
                          <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                            Çmimi minimal
                          </span>

                          <strong className="mt-1 block text-base text-red-600">
                            {formatPrice(
                              minimumAllowedPrice
                            )}{" "}
                            ALL
                          </strong>
                        </div>

                        <div>
                          <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                            Fitimi produktit
                          </span>

                          <strong className="mt-1 block text-base text-blue-700">
                            {formatPrice(productProfit)} ALL
                          </strong>

                          <span className="mt-1 block text-xs text-gray-500">
                            Hyrje: {formatPrice(productPurchasePrice)} ALL
                          </span>
                        </div>

                        <div>
                          <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                            Bonus {formatPrice(productBonusPercent)}%
                          </span>

                          <strong className="mt-1 block text-base text-violet-700">
                            {formatPrice(calculatedBonus)} ALL
                          </strong>

                          <span className="mt-1 block text-xs text-gray-500">
                            Për {Number(form.quantity || 0)} copë
                          </span>
                        </div>
                      </div>

                      <div className="mt-4 grid grid-cols-1 gap-3 border-t border-blue-100 pt-3 sm:grid-cols-2">
                        <div>
                          <span className="text-sm font-semibold text-gray-600">
                            Ulja e propozuar
                          </span>

                          <strong className="mt-1 block text-lg font-extrabold text-blue-700">
                            {formatPrice(proposedDiscountLek)} ALL ({proposedDiscountPercent.toFixed(2)}%)
                          </strong>
                        </div>

                        <div className="sm:text-right">
                          <span className="text-sm font-semibold text-gray-600">
                            Çmimi final për njësi
                          </span>

                          <strong className="mt-1 block text-xl font-extrabold text-emerald-700">
                            {formatPrice(
                              calculatedUnitPrice
                            )}{" "}
                            ALL
                          </strong>
                        </div>
                      </div>
                    </div>
                  )}

                  </div>
                </details>

                <div className="sales-total-preview">
                  <span>Totali i faturës</span>
                  <strong>{formatPrice(invoiceTotal)} ALL</strong>
                </div>

                <details className="sale-optional-section">
                  <summary><span><strong>Shënime</strong><small>Opsionale</small></span><span className="sale-optional-chevron">⌄</span></summary>
                  <div className="sale-optional-content">
                <div className="sales-field">
                  <label htmlFor="notes">
                    Shënime
                  </label>

                  <textarea
                    id="notes"
                    name="notes"
                    rows="3"
                    value={form.notes}
                    onChange={handleInputChange}
                    placeholder="Opsionale..."
                  />
                </div>

                  </div>
                </details>

                <div className="sale-sticky-footer">
                  <div className="sale-footer-total"><span>Totali për pagesë</span><strong>{formatPrice(invoiceTotal)} ALL</strong></div>
                <div className="sales-modal-actions">
                  <button
                    type="button"
                    className="sales-cancel"
                    onClick={closeModal}
                    disabled={saving}
                  >
                    Anulo
                  </button>

                  <button
                    type="submit"
                    className="sales-save"
                    disabled={saving}
                  >
                    <Save size={18} />

                    {saving
                      ? "Duke ruajtur..."
                      : editingId
                        ? "Ruaj ndryshimet"
                        : "Regjistro shitjen"}
                  </button>
                </div>
                </div>
              </form>
            </div>
          </div>
        )}


        {/* =====================================================
          FATURA E SHITJES PËR PRINTIM
      ===================================================== */}


        {/* FLETA E GARANCISË PËR PRINTIM */}
        <div
          style={{
            position: "fixed",
            left: "-10000px",
            top: 0,
          }}
        >
          <div
            ref={warrantyRef}
            className="sales-print-warranty"
            style={{
              width: "194mm",
              maxWidth: "194mm",
              boxSizing: "border-box",
              background: "#ffffff",
              color: "#000000",
              padding: "5mm",
              fontFamily: "Arial, Helvetica, sans-serif",
            }}
          >
            {selectedSale && (
              <>
                <header
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: "24px",
                    paddingBottom: "18px",
                    borderBottom: "2px solid #111827",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "16px",
                    }}
                  >
                    <img
                      src="/logo.png"
                      alt="MasterSol&Tech"
                      style={{
                        width: "150px",
                        maxHeight: "70px",
                        objectFit: "contain",
                      }}
                    />

                    <div>
                      <h1
                        style={{
                          margin: 0,
                          fontSize: "25px",
                          fontWeight: 800,
                        }}
                      >
                        MASTER SOL&TECH
                      </h1>

                      <p
                        style={{
                          margin: "5px 0 0",
                          fontSize: "13px",
                          fontWeight: 600,
                        }}
                      >
                        Shitje dhe servis pajisjesh elektronike
                      </p>

                      <p
                        style={{
                          margin: "4px 0 0",
                          fontSize: "11px",
                        }}
                      >
                        Tel: 0688188188
                      </p>

                      <p
                        style={{
                          margin: "2px 0 0",
                          fontSize: "11px",
                        }}
                      >
                        Adresa: Rruga e Kavajës 133, Tiranë
                      </p>
                    </div>
                  </div>

                  <div style={{ textAlign: "right" }}>
                    <h2
                      style={{
                        margin: 0,
                        fontSize: "21px",
                        fontWeight: 800,
                      }}
                    >
                      FLETË GARANCIE
                    </h2>

                    <p
                      style={{
                        margin: "7px 0 0",
                        fontSize: "12px",
                      }}
                    >
                      NIPTI : M61316023J
                    </p>



                    <p
                      style={{
                        margin: "7px 0 0",
                        fontSize: "12px",
                      }}
                    >
                      Nr. GAR-
                      {selectedSale.created_at
                        ? new Date(
                          selectedSale.created_at
                        ).getFullYear()
                        : new Date().getFullYear()}
                      -
                      {String(selectedSale.id).padStart(
                        6,
                        "0"
                      )}
                    </p>

                    <p
                      style={{
                        margin: "4px 0 0",
                        fontSize: "12px",
                      }}
                    >
                      Data e blerjes:{" "}
                      {selectedSale.created_at
                        ? new Date(
                          selectedSale.created_at
                        ).toLocaleDateString("sq-AL")
                        : "—"}
                    </p>
                  </div>
                </header>



                <section
                  style={{
                    marginTop: "22px",
                    padding: "14px 16px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "12px",
                    lineHeight: 1.7,
                  }}
                >
                  <div style={{ fontWeight: 800, marginBottom: "6px", fontSize: "13px" }}>
                    Të dhënat e klientit
                  </div>
                  <div><strong>Emri:</strong> {selectedSale.customer_name || "—"}</div>
                  <div><strong>Telefoni:</strong> {selectedSale.customer_phone || "—"}</div>
                  <div><strong>Adresa:</strong> {selectedSale.customer_address || "—"}</div>
                  <div><strong>Email:</strong> {selectedSale.customer_email || "—"}</div>
                </section>

                {/* ARTIKULLI NEN GARANCI */}
                <section style={{ marginTop: "26px" }}>
                  <h3
                    style={{
                      margin: "0 0 12px",
                      fontSize: "15px",
                      fontWeight: 800,
                    }}
                  >
                    Artikulli nën garanci
                  </h3>


                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      marginTop: "20px",
                      fontSize: "12px",
                    }}
                  >
                    <thead>
                      <tr style={{ backgroundColor: "#f3f4f6" }}>
                        <th style={{ border: "1px solid #9ca3af", padding: "9px" }}>
                          Nr.
                        </th>

                        {String(
                          selectedSale?.imei || selectedSale?.serial_number || ""
                        ).trim() !== "" && (
                            <th style={{ border: "1px solid #9ca3af", padding: "9px" }}>
                              IMEI / Serial
                            </th>
                          )}

                        <th style={{ border: "1px solid #9ca3af", padding: "9px" }}>
                          Artikulli
                        </th>

                        <th style={{ border: "1px solid #9ca3af", padding: "9px" }}>
                          Sasia
                        </th>

                        <th
                          style={{
                            border: "1px solid #9ca3af",
                            padding: "9px",
                            textAlign: "right",
                          }}
                        >
                          Çmimi
                        </th>

                        <th
                          style={{
                            border: "1px solid #9ca3af",
                            padding: "9px",
                            textAlign: "right",
                          }}
                        >
                          Totali
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      <tr>
                        <td
                          style={{
                            border: "1px solid #9ca3af",
                            padding: "9px",
                            textAlign: "center",
                          }}
                        >
                          1
                        </td>

                        {String(
                          selectedSale?.imei || selectedSale?.serial_number || ""
                        ).trim() !== "" && (
                            <td
                              style={{
                                border: "1px solid #9ca3af",
                                padding: "9px",
                                textAlign: "center",
                              }}
                            >
                              {selectedSale.imei || selectedSale.serial_number}
                            </td>
                          )}

                        <td style={{ border: "1px solid #9ca3af", padding: "9px" }}>
                          {selectedSale?.product_name || "Produkt"}
                        </td>

                        <td
                          style={{
                            border: "1px solid #9ca3af",
                            padding: "9px",
                            textAlign: "center",
                          }}
                        >
                          {selectedSale?.quantity || 0}
                        </td>

                        <td
                          style={{
                            border: "1px solid #9ca3af",
                            padding: "9px",
                            textAlign: "right",
                          }}
                        >
                          {formatPrice(selectedSale?.unit_price)} ALL
                        </td>

                        <td
                          style={{
                            border: "1px solid #9ca3af",
                            padding: "9px",
                            textAlign: "right",
                          }}
                        >
                          {formatPrice(
                            selectedSale?.total ??
                            Number(selectedSale?.unit_price || 0) *
                            Number(selectedSale?.quantity || 0)
                          )} ALL
                        </td>
                      </tr>

                      {/* TOTALI PËRFUNDIMTAR */}
                      <tr>
                        <td
                          colSpan={
                            String(
                              selectedSale?.imei || selectedSale?.serial_number || ""
                            ).trim() !== ""
                              ? 5
                              : 4
                          }
                          style={{
                            textAlign: "right",
                            padding: "12px",
                            fontSize: "16px",
                            fontWeight: 900,
                            backgroundColor: "#e5e7eb",
                            border: "2px solid #111827",
                            color: "#111827",
                          }}
                        >
                          TOTALI PËR PAGESË
                        </td>

                        <td
                          style={{
                            textAlign: "right",
                            padding: "12px",
                            fontSize: "18px",
                            fontWeight: 900,
                            backgroundColor: "#e5e7eb",
                            border: "2px solid #111827",
                            color: "#111827",
                            whiteSpace: "nowrap",
                            WebkitPrintColorAdjust: "exact",
                            printColorAdjust: "exact",
                          }}
                        >
                          {formatPrice(
                            selectedSale?.total ??
                            Number(selectedSale?.unit_price || 0) *
                            Number(selectedSale?.quantity || 0)
                          )} ALL
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </section>

                {/* GARANCIA */}
                <section
                  style={{
                    marginTop: "26px",
                    padding: "18px",
                    border: "2px solid #111827",
                    borderRadius: "8px",
                  }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "32px",
                    }}
                  >
                    <div>
                      <p
                        style={{
                          margin: 0,
                          fontSize: "11px",
                          textTransform: "uppercase",
                          color: "#6b7280",
                          fontWeight: 700,
                        }}
                      >
                        Kohëzgjatja e garancisë
                      </p>

                      <p
                        style={{
                          margin: "5px 0 0",
                          fontSize: "24px",
                          fontWeight: 800,
                        }}
                      >
                        {selectedSale.warranty_months ||
                          0}{" "}
                        muaj
                      </p>
                    </div>

                    <div>
                      <p
                        style={{
                          margin: 0,
                          fontSize: "11px",
                          textTransform: "uppercase",
                          color: "#6b7280",
                          fontWeight: 700,
                        }}
                      >
                        Garancia përfundon
                      </p>

                      <p
                        style={{
                          margin: "5px 0 0",
                          fontSize: "24px",
                          fontWeight: 800,
                        }}
                      >
                        {selectedSale.created_at &&
                          Number(
                            selectedSale.warranty_months
                          ) > 0
                          ? (() => {
                            const warrantyDate =
                              new Date(
                                selectedSale.created_at
                              );

                            warrantyDate.setMonth(
                              warrantyDate.getMonth() +
                              Number(
                                selectedSale.warranty_months
                              )
                            );

                            return warrantyDate.toLocaleDateString(
                              "sq-AL"
                            );
                          })()
                          : "—"}
                      </p>
                    </div>
                  </div>
                </section>

                {/* KUSHTET */}
                <section
                  style={{
                    marginTop: "26px",
                    fontSize: "12px",
                    lineHeight: 1.7,
                  }}
                >
                  <h3
                    style={{
                      margin: "0 0 10px",
                      fontSize: "14px",
                      fontWeight: 800,
                    }}
                  >
                    Kushtet e garancisë
                  </h3>

                  <p style={{ margin: "4px 0" }}>
                    1. Garancia mbulon vetëm defektet
                    teknike të pajisjes gjatë periudhës
                    së përcaktuar.
                  </p>

                  <p style={{ margin: "4px 0" }}>
                    2. Garancia nuk mbulon thyerje,
                    goditje, lagështi, kontakt me lëngje,
                    djegie elektrike ose dëmtime fizike.
                  </p>

                  <p style={{ margin: "4px 0" }}>
                    3. Garancia humbet nëse pajisja hapet
                    ose riparohet nga persona ose servise
                    të paautorizuara.
                  </p>

                  <p style={{ margin: "4px 0" }}>
                    4. Klienti duhet të paraqesë këtë
                    dokument dhe faturën e shitjes për çdo
                    kërkesë garancie.
                  </p>

                  <p style={{ margin: "4px 0" }}>
                    5. Të dhënat personale të ruajtura në
                    pajisje nuk mbulohen nga garancia.
                  </p>
                </section>

                {/* SHENIME */}
                {selectedSale.notes && (
                  <section
                    style={{
                      marginTop: "24px",
                      padding: "12px",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      minHeight: "55px",
                      fontSize: "12px",
                    }}
                  >
                    <strong>Shënime:</strong>

                    <div style={{ marginTop: "6px" }}>
                      {selectedSale.notes}
                    </div>
                  </section>
                )}



                <p
                  style={{
                    marginTop: "28px",
                    paddingTop: "10px",
                    borderTop: "1px solid #d1d5db",
                    textAlign: "center",
                    fontSize: "10px",
                    color: "#6b7280",
                    width: "100%",
                  }}
                >
                  Dokument i gjeneruar elektronikisht nga MasterSol&Tech.
                </p>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
const salesDesign = `
/* Shitjet — ndryshon vetëm pamja;
dokumentet e printimit ruhen. */
.sales-design {
  background: #f9fafb;
  color: #111827;
  font-family: inherit;
}

.sales-design main>.mb-6.flex {
  background: transparent;
  border: 0;
  box-shadow: none;
  padding: 0;
}

.sales-design main>.mb-6.flex h1 {
  font-size: 30px;
  line-height: 1.25;
  font-weight: 700;
  color: #111827;
  letter-spacing: normal;
}

.sales-design main>.mb-6.flex p {
  font-size: 14px;
  color: #6b7280;
  margin-top: 6px;
}

.sales-design .sales-new-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #2563eb;
  border: 1px solid #2563eb;
  border-radius: 12px;
  color: white;
  padding: 12px 20px;
  font-size: 14px;
  font-weight: 600;
  box-shadow: none;
}

.sales-design .sales-new-button:hover {
  background: #1d4ed8;
}

.sales-design .sales-list-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: none;
}

.sales-design .sales-list-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 18px;
  padding: 22px 24px;
  border-bottom: 1px solid #e5e7eb;
}

.sales-design .sales-list-toolbar h2 {
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 5px;
}

.sales-design .sales-list-toolbar p {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
}

.sales-design .sales-search {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 360px;
  min-height: 44px;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  padding: 10px 13px;
  color: #9ca3af;
}

.sales-design .sales-search input {
  width: 100%;
  min-width: 0;
  border: 0;
  outline: 0;
  background: transparent;
  font-family: inherit;
  font-size: 14px;
  color: #374151;
}

.sales-design .sales-table {
  width: 100%;
  border-collapse: collapse;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.5;
}

.sales-design .sales-table thead {
  background: #f9fafb;
  color: #6b7280;
  border-bottom: 1px solid #e5e7eb;
}

.sales-design .sales-table th {
  padding: 16px 20px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: .4px;
  text-transform: uppercase;
  text-align: left;
}

.sales-design .sales-table td {
  padding: 18px 20px;
  border-bottom: 1px solid #f3f4f6;
  font-size: 14px;
  color: #374151;
}

.sales-design .sales-table tbody tr:hover {
  background: #f9fafb;
}

.sales-design .sales-table .sales-total {
  font-weight: 600;
  color: #2563eb;
}

.sales-design .sales-type-badge {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 6px 11px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: normal;
}

.sales-design .sales-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.sales-design .sales-actions button {
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}

.sales-design .sales-edit {
  background: #eff6ff;
  color: #2563eb;
}

.sales-design .sales-delete {
  background: #fef2f2;
  color: #dc2626;
}

.sales-design .sales-empty {
  padding: 48px 20px;
  text-align: center;
  font-size: 14px;
  color: #6b7280;
}

.sales-design .sales-message {
  padding: 14px 18px;
  border-radius: 12px;
  font-size: 14px;
}

.sales-design .sales-mobile-card {
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  box-shadow: none;
  background: white;
}

.sales-design .sales-mobile-product-name {
  font-size: 16px;
  font-weight: 600;
  line-height: 1.5;
  color: #111827;
}

.sales-design .sales-mobile-info,.sales-design .sales-mobile-footer {
  font-size: 14px;
  color: #6b7280;
}

.sales-design .sales-mobile-details-link {
  font-size: 13px;
  font-weight: 600;
  color: #2563eb;
}

.sales-design .sales-modal-overlay,.sales-design .sales-detail-overlay {
  background: #15243a88;
  backdrop-filter: blur(4px);
}

.sales-design .sales-modal,.sales-design .sales-detail-modal {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  font-family: inherit;
  color: #111827;
  box-shadow: 0 30px 100px #182c5040;
}

.sales-design .sales-modal-header,.sales-design .sales-detail-header {
  background: #f9fafb;
  color: #111827;
  border-bottom: 1px solid #e5e7eb;
  padding: 22px 24px;
}

.sales-design .sales-modal-header h2,.sales-design .sales-detail-header h2 {
  font-size: 22px;
  font-weight: 700;
  letter-spacing: normal;
}

.sales-design .sales-modal-eyebrow {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
}

.sales-design .sales-modal-close {
  background: white;
  color: #6b7280;
  border: 1px solid #e5e7eb;
  border-radius: 9px;
}

.sales-design .sales-field {
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
}

.sales-design .sales-field label {
  font-size: 14px;
  font-weight: 500;
  color: #4b5563;
}

.sales-design .sales-field input,.sales-design .sales-field textarea,.sales-design .sales-field select {
  font-family: inherit;
  font-size: 14px;
  font-weight: 400;
  color: #374151;
  border-color: #d1d5db;
  border-radius: 10px;
}

.sales-design .sales-detail-grid strong {
  font-size: 14px;
  font-weight: 600;
}

.sales-design .sales-detail-grid span,.sales-design .sales-detail-grid label {
  font-size: 12px;
  color: #6b7280;
}

.sales-design .sales-modal-actions button,.sales-design .sales-detail-actions button,.sales-design .sales-mode-switch button {
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  border-radius: 10px;
  box-shadow: none;
}

.sales-design .sales-save {
  background: #2563eb;
  color: #fff;
}

.sales-design .sales-total-preview {
  border-radius: 12px;
  font-size: 14px;
}

.sales-design .sales-table button:focus-visible,.sales-design .sales-new-button:focus-visible {
  outline: 2px solid #5385ef;
  outline-offset: 3px;
}

@media(max-width:767px) {
  .sales-design main>.mb-6.flex h1 {
    font-size: 26px;
  }

  .sales-design .sales-list-toolbar {
    padding: 18px 16px;
    align-items: stretch;
  }

  .sales-design .sales-list-toolbar h2 {
    font-size: 17px;
  }

  .sales-design .sales-search {
    width: 100%;
  }

  .sales-design .sales-modal-header,.sales-design .sales-detail-header {
    padding: 18px;
  }

  .sales-design .sales-modal-header h2,.sales-design .sales-detail-header h2 {
    font-size: 20px;
  }

}

.sales-design main > .mb-6.flex > div > span {
  display: block;
  margin-bottom: 6px;
}

.sales-design main>.mb-6.flex h1 {
  margin-top: 0;
}

.sales-design main {
  font-size: 14px;
  line-height: 1.5;
}

.sales-design .sales-list-toolbar {
  background: #fff;
}

.sales-design .sales-list-toolbar h2 {
  font-size: 16px;
  font-weight: 600;
}

.sales-design .sales-table-wrapper {
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
}

.sales-design .sales-table {
  min-width: 1100px;
}

.sales-design .sales-table td strong {
  font-weight: 600;
}

.sales-design .sales-table td button {
  font-family: inherit;
}

.sales-design .sales-modal-header,.sales-design .sales-detail-header {
  background-image: none;
}

.sales-design .sales-modal-header p,.sales-design .sales-detail-header p {
  font-size: 14px;
  color: #6b7280;
}

.sales-design .sales-modal-actions,.sales-design .sales-detail-actions {
  background: #fff;
  border-top: 1px solid #e5e7eb;
}

.sales-design .sales-mode-switch {
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  border-radius: 12px;
  padding: 4px;
}

.sales-design .sales-form input:focus,.sales-design .sales-form select:focus,.sales-design .sales-form textarea:focus {
  outline: 2px solid #bfdbfe;
  outline-offset: 1px;
}

.sales-design .sales-mobile-card {
  box-shadow: 0 1px 2px rgb(0 0 0 / .025);
}

/* Pamja sipas referencës së Furnitorëve. */
.sales-design .sales-filter-panel {
  margin-bottom: 20px;
  background: #fff;
  border: 1px solid #dfe6f0;
  border-radius: 16px;
  box-shadow: 0 1px 2px rgb(15 23 42 / .05);
  padding: 16px;
}

.sales-design .sales-filter-panel .sales-list-toolbar {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 16px;
  padding: 0;
  border: 0;
  border-radius: 0;
  box-shadow: none;
}

.sales-design .sales-filter-panel .sales-list-toolbar>div:first-child {
  padding-bottom: 16px;
  border-bottom: 1px solid #edf1f6;
}

.sales-design .sales-list-toolbar h2 {
  font-size: 18px;
  line-height: 1.5;
  font-weight: 600;
}

.sales-design .sales-filter-panel .sales-search {
  width: 100%;
  max-width: none;
  min-height: 46px;
  height: 46px;
  padding: 10px 16px;
  border: 1px solid #cbd5e1;
  border-radius: 11px;
  box-shadow: none;
}

.sales-design .sales-search input {
  padding: 0;
  height: auto;
  min-height: 0;
  box-shadow: none;
  font-size: 14px;
}

.sales-design .sales-list-card {
  border: 1px solid #dfe6f0;
  box-shadow: none;
  border-radius: 16px;
}

.sales-design .sales-table-wrapper {
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: 0;
}

.sales-design .sales-table {
  margin: 0;
  border: 0;
  border-radius: 0;
}

.sales-design .sales-table thead,.sales-design .sales-table thead tr,.sales-design .sales-table thead th {
  background: #f8fafc!important;
  color: #64748b!important;
  background-image: none!important;
}

.sales-design .sales-table thead th {
  padding: 16px 20px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: .4px;
  border-bottom: 1px solid #dfe6f0;
}

.sales-design .sales-table tbody td {
  padding: 20px;
  font-size: 14px;
  border-bottom: 1px solid #edf1f6;
}

.sales-design .sales-table tbody tr:last-child td {
  border-bottom: 0;
}

.sales-design .sales-table .sales-total {
  color: #2563eb;
  font-weight: 600;
}

@media(max-width:767px) {
  .sales-design .sales-filter-panel {
    padding: 16px;
  }

  .sales-design .sales-filter-panel .sales-list-toolbar {
    padding: 0;
  }

  .sales-design .sales-filter-panel .sales-search {
    width: 100%;
  }

}

.sales-design .sales-view {
  background: #f8fafc;
  color: #475569;
}

.sales-design .sales-view:hover {
  background: #eef2f7;
}

.sales-design .sales-detail-overlay {
  position: fixed;
  inset: 0;
  z-index: 9998;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.sales-design .sales-detail-modal {
  width: 100%;
  max-width: 760px;
  max-height: 92dvh;
  overflow-y: auto;
}

.sales-design .sales-name-link {
  border: 0;
  padding: 0;
  background: transparent;
  color: #111827;
  font: inherit;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
}

.sales-design .sales-name-link:hover {
  color: #2563eb;
  text-decoration: underline;
}

.sales-design .sales-name-link:focus-visible,
.sales-design .sales-mobile-card:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: 3px;
}

/* Tabela kompakte dhe dialogu i plotë në çdo ekran. */
.sales-design .sales-table {
  min-width: 1050px;
  table-layout: auto;
}

.sales-design .sales-name-link {
  display: block;
  width: 210px;
  max-width: 210px;
  white-space: normal;
  overflow-wrap: anywhere;
  line-height: 1.5;
}

.sales-design .sales-table td {
  vertical-align: middle;
}

.sales-design .sales-detail-modal {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.sales-design .sales-detail-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-shrink: 0;
}

.sales-design .sales-detail-header > div {
  min-width: 0;
}

.sales-design .sales-detail-header span {
  display: block;
  margin-bottom: 6px;
  font-size: 12px;
  color: #64748b;
}

.sales-design .sales-detail-header h2 {
  margin: 0;
  line-height: 1.4;
  overflow-wrap: anywhere;
}

.sales-design .sales-detail-header > button {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
  color: #64748b;
  font-size: 24px;
  cursor: pointer;
}

.sales-design .sales-detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  padding: 24px;
  overflow-y: auto;
  min-height: 0;
}

.sales-design .sales-detail-grid > div {
  padding: 14px 16px;
  border: 1px solid #edf1f6;
  border-radius: 12px;
  background: #f8fafc;
  min-width: 0;
}

.sales-design .sales-detail-grid span {
  display: block;
  margin-bottom: 6px;
  font-size: 12px;
  color: #64748b;
}

.sales-design .sales-detail-grid strong {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  overflow-wrap: anywhere;
  line-height: 1.6;
}

.sales-design .sales-detail-grid .detail-total {
  color: #2563eb;
}

.sales-design .sales-detail-notes {
  grid-column: 1 / -1;
  white-space: pre-wrap;
}

.sales-design .sales-detail-actions {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 10px;
  padding: 16px 24px;
  flex-shrink: 0;
}

.sales-design .sales-detail-actions button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 42px;
  padding: 10px 16px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #475569;
  cursor: pointer;
}

.sales-design .sales-detail-actions .warranty {
  background: #f5f3ff;
  color: #7c3aed;
  border-color: #ddd6fe;
}

.sales-design .sales-detail-actions .edit {
  background: #eff6ff;
  color: #2563eb;
  border-color: #bfdbfe;
}

.sales-design .sales-detail-actions .delete {
  background: #fef2f2;
  color: #dc2626;
  border-color: #fecaca;
}

@media (max-width: 600px) {
  .sales-design .sales-detail-overlay {
    padding: 10px;
  }

  .sales-design .sales-detail-grid {
    padding: 16px;
    gap: 10px;
  }

  .sales-design .sales-detail-grid > div {
    padding: 12px;
  }

  .sales-design .sales-detail-actions {
    padding: 14px 16px;
    justify-content: stretch;
  }

  .sales-design .sales-detail-actions button {
    flex: 1 1 auto;
  }
}

@media (max-width: 380px) {
  .sales-design .sales-detail-grid {
    grid-template-columns: 1fr;
  }
}

/* Mobile-first sales entry dialog */
.sales-design .sale-entry-modal { width:min(920px,calc(100vw - 32px)); max-width:920px; max-height:min(92dvh,900px); display:flex; flex-direction:column; overflow:hidden; }
.sales-design .sale-entry-modal .sales-modal-header { flex-shrink:0; padding:16px 22px; background:white; }
.sales-design .sale-entry-modal .sales-form { overflow-y:auto; overscroll-behavior:contain; padding:18px 22px 0; display:flex; flex-direction:column; gap:14px; }
.sales-design .sale-entry-modal .sales-field input,.sales-design .sale-entry-modal .sales-field select,.sales-design .sale-entry-modal .sales-field textarea { min-height:44px; font-size:16px; }
.sales-design .sale-quick-guide { display:flex; align-items:center; gap:12px; background:#eff6ff; border:1px solid #bfdbfe; border-radius:14px; padding:12px; }
.sales-design .sale-quick-guide-number { display:grid; place-items:center; width:30px; height:30px; flex-shrink:0; background:#2563eb; color:white; border-radius:50%; font-weight:800; }
.sales-design .sale-quick-guide strong { display:block; color:#1e3a8a; font-size:14px; }
.sales-design .sale-quick-guide p { font-size:12px; line-height:1.45; color:#1e40af; }
.sales-design .sale-quick-guide-count { margin-left:auto; white-space:nowrap; font-size:11px; font-weight:700; color:#1d4ed8; }
.sales-design .sale-cart-panel { border:1px solid #bfdbfe; border-radius:16px; background:#fff; padding:14px; }
.sales-design .sale-cart-heading { display:flex; align-items:center; justify-content:space-between; gap:10px; padding:0 0 12px; }
.sales-design .sale-cart-heading strong { font-size:15px; color:#111827; }
.sales-design .sale-cart-heading p { font-size:12px; color:#64748b; }
.sales-design .sale-cart-heading>span { white-space:nowrap; background:#eff6ff; color:#1d4ed8; font-size:11px; padding:6px 9px; border-radius:20px; font-weight:700; }
.sales-design .sale-optional-section { border:1px solid #e2e8f0; border-radius:14px; background:white; overflow:visible; }
.sales-design .sale-optional-section>summary { cursor:pointer; list-style:none; display:flex; justify-content:space-between; align-items:center; padding:13px 15px; }
.sales-design .sale-optional-section>summary::-webkit-details-marker { display:none; }
.sales-design .sale-optional-section>summary strong { display:block; font-size:14px; color:#0f172a; }
.sales-design .sale-optional-section>summary small { display:block; font-size:12px; color:#64748b; margin-top:2px; }
.sales-design .sale-optional-chevron { color:#64748b; font-size:22px; transition:transform .2s; }
.sales-design .sale-optional-section[open] .sale-optional-chevron { transform:rotate(180deg); }
.sales-design .sale-optional-content { border-top:1px solid #e2e8f0; padding:15px; display:flex; flex-direction:column; gap:12px; }
.sales-design .sale-sticky-footer { position:sticky; bottom:0; z-index:8; background:white; margin:4px -22px 0; padding:12px 22px max(12px,env(safe-area-inset-bottom)); border-top:1px solid #e2e8f0; box-shadow:0 -5px 18px #0f172a0d; }
.sales-design .sale-footer-total { display:flex; justify-content:space-between; align-items:center; margin-bottom:8px; gap:12px; }
.sales-design .sale-footer-total span { font-size:12px; color:#64748b; font-weight:600; }
.sales-design .sale-footer-total strong { font-size:20px; color:#0f766e; }
.sales-design .sale-sticky-footer .sales-modal-actions { margin:0; padding:0; display:flex; gap:10px; }
.sales-design .sale-sticky-footer .sales-modal-actions button { min-height:46px; }
.sales-design .sale-sticky-footer .sales-save { flex:1; }
@media(max-width:640px) {
  .sales-design .sale-entry-modal { position:fixed; inset:0; width:100%; max-width:none; max-height:100dvh; height:100dvh; border:0; border-radius:0; }
  .sales-design .sale-entry-modal .sales-modal-header { padding:12px 16px; }
  .sales-design .sale-entry-modal .sales-modal-header h2 { font-size:19px; }
  .sales-design .sale-entry-modal .sales-form { padding:12px 14px 0; gap:12px; }
  .sales-design .sale-quick-guide { align-items:flex-start; padding:10px; }
  .sales-design .sale-quick-guide-count { display:none; }
  .sales-design .sale-cart-panel { padding:12px; }
  .sales-design .sale-sticky-footer { margin:4px -14px 0; padding:10px 14px max(10px,env(safe-area-inset-bottom)); }
  .sales-design .sale-footer-total strong { font-size:19px; }
  .sales-design .sale-sticky-footer .sales-modal-actions .sales-cancel { flex:0 0 auto; padding:10px 13px; }
  .sales-design .sale-sticky-footer .sales-modal-actions .sales-save { padding:10px 12px; }
}

`;